import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Calendar, Clock, Users, Video, Plus, X, Edit2, Trash2, MapPin, User, ExternalLink, FileText, ChevronUp, ChevronDown, FileAudio, Download, Loader, Mic, Upload, CheckCircle, AlertCircle, RefreshCw, Pencil, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { apiCall, mediaAPI, apiUpload } from '../services/api';
import { realEstateMeetingsMockData, transcribeConversationsMockData } from '../data/mockData'; // Import mockup data
import * as XLSX from 'xlsx';
import { toast } from 'sonner';

// Convert 24-hour time to 12-hour format with AM/PM
const formatTimeTo12Hour = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  let hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${hour}:${minutes} ${ampm}`;
};

const RealEstateManagingPartnerMeetings = () => {
  const location = useLocation();
  
  // API URL Configuration - always use same origin
  const apiUrl = typeof window !== 'undefined' ? window.location.origin : '';
  
  const [meetings, setMeetings] = useState([]);
  const [smartsheetMeetings, setSmartsheetMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingMeetingId, setEditingMeetingId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [selectedOrgMeetings, setSelectedOrgMeetings] = useState([]); // Store all meetings for selected org
  const [meetingTranscriptions, setMeetingTranscriptions] = useState({}); // Map meeting ID to transcription ID
  const [showTranscriptionPopup, setShowTranscriptionPopup] = useState(false);
  const [viewingTranscription, setViewingTranscription] = useState(null);
  const [sortField, setSortField] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc'); // desc = newest first (default)
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' or 'list'
  const [draggedMeeting, setDraggedMeeting] = useState(null);
  
  // Meeting status state (upcoming, in_progress, done)
  const [meetingStatuses, setMeetingStatuses] = useState(() => {
    const saved = localStorage.getItem('meeting_statuses');
    return saved ? JSON.parse(saved) : {};
  });
  
  // Transcription states
  const [conversations, setConversations] = useState([]);
  const [selectedTranscriptCategory, setSelectedTranscriptCategory] = useState('all');
  const [expandedConversation, setExpandedConversation] = useState(null);
  const [isUploadExpanded, setIsUploadExpanded] = useState(false);
  const [draggedTranscription, setDraggedTranscription] = useState(null);
  
  // Excel upload states
  const [dataSource, setDataSource] = useState('excel'); // 'smartsheet', 'excel', or 'personal' - Default to Excel
  const [excelFile, setExcelFile] = useState(null);
  const [excelMeetings, setExcelMeetings] = useState([]);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [personalMeetings, setPersonalMeetings] = useState([]);
  
  // Drag and drop for reordering within columns
  const [draggedCardIndex, setDraggedCardIndex] = useState(null);
  const [draggedColumnId, setDraggedColumnId] = useState(null);
  
  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioLevels, setAudioLevels] = useState(Array(12).fill(0));
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  
  // File upload states
  const [selectedFile, setSelectedFile] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const [transcription, setTranscription] = useState(null);
  const [transcriptionError, setTranscriptionError] = useState(null);
  const [language, setLanguage] = useState('');
  
  // Document upload states for meetings
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [documentUploadMeetingId, setDocumentUploadMeetingId] = useState(null);
  
  const supportedFormats = ['mp3', 'mp4', 'mpeg', 'mpga', 'm4a', 'wav', 'webm'];
  const supportedDocFormats = ['pdf', 'doc', 'docx', 'txt', 'xls', 'xlsx', 'ppt', 'pptx', 'png', 'jpg', 'jpeg'];

  // Default meeting categories
  const defaultMeetingCategories = [
    { id: 'all', name: 'All', icon: '📋', color: 'gray' },
    { id: 'real-estate', name: 'Real Estate', icon: '🏢', color: 'blue' },
    { id: 'investments', name: 'Investments', icon: '💰', color: 'green' },
    { id: 'speeches', name: 'Speeches', icon: '🎤', color: 'purple' },
    { id: 'legal', name: 'Legal', icon: '⚖️', color: 'red' },
    { id: 'personal', name: 'Personal', icon: '👤', color: 'cyan' }
  ];
  
  // Load custom meeting categories from localStorage
  const [customMeetingCategories, setCustomMeetingCategories] = useState(() => {
    try {
      const saved = localStorage.getItem('meeting_categories');
      return saved ? JSON.parse(saved) : defaultMeetingCategories;
    } catch { return defaultMeetingCategories; }
  });
  
  // Editable category state
  const [showMeetingCategoryModal, setShowMeetingCategoryModal] = useState(false);
  const [newMeetingCategoryName, setNewMeetingCategoryName] = useState('');
  const [newMeetingCategoryIcon, setNewMeetingCategoryIcon] = useState('📅');
  const [newMeetingCategoryColor, setNewMeetingCategoryColor] = useState('blue');
  const [editingMeetingCategory, setEditingMeetingCategory] = useState(null);
  const [editMeetingCategoryValue, setEditMeetingCategoryValue] = useState('');
  
  // Available emoji icons for meeting categories
  const meetingCategoryIcons = ['📋', '🏢', '💰', '🎤', '⚖️', '👤', '📅', '💼', '🤝', '📊', '🎯', '💡', '🔔', '⏰', '📱', '💻', '🏠', '✈️', '🎓', '⚽', '🎨', '🎵', '🍽️', '🛒', '⚕️', '🔧', '📁', '🌟', '🎉', '📧'];
  
  // Available colors for categories
  const meetingCategoryColors = ['blue', 'green', 'purple', 'red', 'cyan', 'orange', 'pink', 'yellow', 'indigo', 'teal', 'emerald', 'gray'];
  
  // Category management functions
  const addMeetingCategory = () => {
    if (!newMeetingCategoryName.trim()) return;
    
    const categoryId = newMeetingCategoryName.toLowerCase().replace(/\s+/g, '-');
    
    if (customMeetingCategories.some(c => c.id === categoryId)) {
      toast.error('Category already exists');
      return;
    }
    
    const newCategory = {
      id: categoryId,
      name: newMeetingCategoryName,
      icon: newMeetingCategoryIcon,
      color: newMeetingCategoryColor
    };
    
    const updatedCategories = [...customMeetingCategories, newCategory];
    setCustomMeetingCategories(updatedCategories);
    localStorage.setItem('meeting_categories', JSON.stringify(updatedCategories));
    setNewMeetingCategoryName('');
    setNewMeetingCategoryIcon('📅');
    setNewMeetingCategoryColor('blue');
    toast.success(`Added "${newMeetingCategoryName}" category`);
  };
  
  const updateMeetingCategory = (oldId, newName, newIcon, newColor) => {
    if (!newName.trim()) return;
    const newId = newName.toLowerCase().replace(/\s+/g, '-');
    
    const updatedCategories = customMeetingCategories.map(c => {
      if (c.id === oldId) {
        return { ...c, id: newId, name: newName, icon: newIcon || c.icon, color: newColor || c.color };
      }
      return c;
    });
    
    setCustomMeetingCategories(updatedCategories);
    localStorage.setItem('meeting_categories', JSON.stringify(updatedCategories));
    if (selectedCategory === oldId) setSelectedCategory(newId);
    setEditingMeetingCategory(null);
    setEditMeetingCategoryValue('');
    toast.success(`Category renamed to "${newName}"`);
  };
  
  const deleteMeetingCategory = (categoryId) => {
    const protectedCategories = ['all', 'real-estate', 'personal'];
    if (protectedCategories.includes(categoryId)) {
      toast.error('Cannot delete default category');
      return;
    }
    
    const updatedCategories = customMeetingCategories.filter(c => c.id !== categoryId);
    setCustomMeetingCategories(updatedCategories);
    localStorage.setItem('meeting_categories', JSON.stringify(updatedCategories));
    if (selectedCategory === categoryId) setSelectedCategory('all');
    toast.success('Category deleted');
  };
  
  // Get color classes for category
  const getMeetingCategoryColorClasses = (color) => {
    const colorMap = {
      blue: 'bg-blue-600 border-blue-500',
      green: 'bg-green-600 border-green-500',
      purple: 'bg-purple-600 border-purple-500',
      red: 'bg-red-600 border-red-500',
      cyan: 'bg-cyan-600 border-cyan-500',
      orange: 'bg-orange-600 border-orange-500',
      pink: 'bg-pink-600 border-pink-500',
      yellow: 'bg-yellow-600 border-yellow-500',
      indigo: 'bg-indigo-600 border-indigo-500',
      teal: 'bg-teal-600 border-teal-500',
      emerald: 'bg-emerald-600 border-emerald-500',
      gray: 'bg-gray-600 border-gray-500'
    };
    return colorMap[color] || 'bg-gray-600 border-gray-500';
  };
  
  // Legacy categories array for backward compatibility (maps to custom categories)
  const categories = customMeetingCategories.map(c => c.name);

  // Deal pipeline stages
  const pipelineStages = [
    { id: 'tbd', name: 'TBD', color: 'bg-blue-600' },
    { id: 'upcoming', name: 'Upcoming', color: 'bg-yellow-600' },
    { id: 'in_progress', name: 'In Progress', color: 'bg-orange-600' },
    { id: 'done', name: 'Done', color: 'bg-green-600' }
  ];

  // Auto-calculate pipeline stage based on meeting date
  const calculatePipelineStage = (meeting) => {
    const meetingDate = meeting.date;
    
    // If no date or TBD, return 'tbd'
    if (!meetingDate || meetingDate === 'TBD' || meetingDate === '-' || meetingDate === '') {
      return 'tbd';
    }
    
    // Parse the date - handle "2025-11-06 00:00:00" format
    const cleanDate = (meetingDate || '').split(' ')[0];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const meetingDateObj = new Date(cleanDate);
    meetingDateObj.setHours(0, 0, 0, 0);
    
    // Check if date is valid
    if (isNaN(meetingDateObj.getTime())) {
      return 'tbd';
    }
    
    // Compare dates
    if (meetingDateObj.getTime() === today.getTime()) {
      return 'in_progress'; // Meeting is today
    } else if (meetingDateObj > today) {
      return 'upcoming'; // Meeting is in the future
    } else {
      return 'done'; // Meeting date has passed
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, meeting) => {
    setDraggedMeeting(meeting);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetStage) => {
    e.preventDefault();
    if (!draggedMeeting) return;

    // Update meeting pipeline stage
    const updatedMeetings = meetings.map(m => 
      m.id === draggedMeeting.id 
        ? { ...m, pipeline_stage: targetStage }
        : m
    );
    
    setMeetings(updatedMeetings);
    setDraggedMeeting(null);
    
    // Also update the meeting status to match the column
    updateMeetingStatus(draggedMeeting.id, targetStage);
    
    // Save to localStorage
    const stagesMap = updatedMeetings.reduce((acc, m) => {
      acc[m.id] = m.pipeline_stage || 'tbd';
      return acc;
    }, {});
    
    localStorage.setItem('meeting_pipeline_stages', JSON.stringify(stagesMap));
    
    // If Excel data source, also save to backend for persistence
    if (dataSource === 'excel') {
      saveExcelPipelineStages(updatedMeetings);
    }
    
    console.log(`✅ Moved ${draggedMeeting.title} to ${targetStage}`);
  };
  
  const saveExcelPipelineStages = async (meetingsData) => {
    try {
      // Send updated pipeline stages to backend
      await fetch(`${apiUrl}/api/excel/save-pipeline-stages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          stages: meetingsData.reduce((acc, m) => {
            acc[m.id] = m.pipeline_stage || 'tbd';
            return acc;
          }, {})
        })
      });
      console.log('✅ Pipeline stages saved to backend');
    } catch (error) {
      console.error('Failed to save pipeline stages to backend:', error);
      // Continue anyway - localStorage is the backup
    }
  };

  // Update meeting status (upcoming, in_progress, done)
  const updateMeetingStatus = async (meetingId, newStatus) => {
    const updatedStatuses = {
      ...meetingStatuses,
      [meetingId]: newStatus
    };
    setMeetingStatuses(updatedStatuses);
    localStorage.setItem('meeting_statuses', JSON.stringify(updatedStatuses));
    
    // Also save to backend for persistence
    try {
      await fetch(`${apiUrl}/api/excel/save-meeting-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          meeting_id: meetingId,
          status: newStatus
        })
      });
      console.log(`✅ Meeting status updated to ${newStatus}`);
    } catch (error) {
      console.error('Failed to save meeting status to backend:', error);
    }
  };

  // Get meeting status with fallback to calculated status
  const getMeetingDisplayStatus = (meeting) => {
    // First check if there's a manually set status
    if (meetingStatuses[meeting.id]) {
      return meetingStatuses[meeting.id];
    }
    // Fall back to auto-calculated pipeline stage based on date
    return calculatePipelineStage(meeting);
  };

  const saveExcelCardOrder = async (cardOrder) => {
    try {
      // Send updated card order to backend
      await fetch(`${apiUrl}/api/excel/save-card-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          card_order: cardOrder
        })
      });
      console.log('✅ Card order saved to backend');
    } catch (error) {
      console.error('Failed to save card order to backend:', error);
      // Continue anyway - localStorage is the backup
    }
  };

  const handleDragEnd = () => {
    setDraggedMeeting(null);
  };
  const transcriptCategories = ['All', 'Business', 'Personal', 'Meetings', 'Interviews', 'Calls', 'Other'];

  // Form states
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    duration: 60,
    meeting_link: '',
    location: '',
    meeting_type: 'virtual',
    category: 'Personal',
    notes: '',
    agenda: '',
    transcript: '',
    transcription_id: '',
    participants: []
  });
  const [newParticipant, setNewParticipant] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    phone: '',
    image_url: '',
    image_file: null
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    // Default to Excel - always try to load Excel data first
    const loadExcelData = async () => {
      try {
        // First try the new excel_meetings collection
        const response = await apiCall('/excel/get-desktop-data');
        if (response.meetings && response.meetings.length > 0) {
          setExcelMeetings(response.meetings);
          
          const savedStages = JSON.parse(localStorage.getItem('meeting_pipeline_stages') || '{}');
          const meetingsWithStages = response.meetings.map(m => ({
            ...m,
            pipeline_stage: savedStages[m.id] || m.pipeline_stage || 'prospecting'
          }));
          setMeetings(meetingsWithStages);
          console.log('✅ Loaded Excel data from backend (excel_meetings collection)');
          
          // Also load saved card order from backend
          try {
            const cardOrderResponse = await apiCall('/excel/get-card-order');
            if (cardOrderResponse.card_order && Object.keys(cardOrderResponse.card_order).length > 0) {
              localStorage.setItem('kanban_card_order', JSON.stringify(cardOrderResponse.card_order));
              console.log('✅ Loaded card order from backend');
            }
          } catch (err) {
            console.log('No saved card order found in backend');
          }
          
          // Load saved meeting statuses from backend
          try {
            const statusesResponse = await apiCall('/excel/get-meeting-statuses');
            if (statusesResponse.statuses && Object.keys(statusesResponse.statuses).length > 0) {
              setMeetingStatuses(statusesResponse.statuses);
              localStorage.setItem('meeting_statuses', JSON.stringify(statusesResponse.statuses));
              console.log('✅ Loaded meeting statuses from backend');
            }
          } catch (err) {
            console.log('No saved meeting statuses found in backend');
          }
        } else {
          // Fallback: Try the meetings collection (for data synced with old endpoint)
          console.log('No data in excel_meetings, checking meetings collection...');
          try {
            const meetingsResponse = await apiCall('/meetings/');
            if (Array.isArray(meetingsResponse) && meetingsResponse.length > 0) {
              // Filter for Excel-sourced meetings
              const excelMeetings = meetingsResponse.filter(m => 
                m.source === 'desktop_excel' || 
                m.source === 'desktop_excel_mac' ||
                (m.source && m.source.includes('excel'))
              );
              
              if (excelMeetings.length > 0) {
                // Transform to expected format
                const transformedMeetings = excelMeetings.map(m => ({
                  ...m,
                  id: m.id || m._id,
                  title: m.title || `Meeting with ${m.participants?.[0] || 'Contact'}`,
                  organization: m.company || m.organization || m.location || '',
                  pipeline_stage: m.pipeline_stage || 'prospecting',
                  participants: m.participants ? 
                    (Array.isArray(m.participants) && typeof m.participants[0] === 'string' 
                      ? m.participants.map(p => ({ name: p, company: m.company || '' }))
                      : m.participants)
                    : []
                }));
                
                setExcelMeetings(transformedMeetings);
                setMeetings(transformedMeetings);
                console.log(`✅ Loaded ${transformedMeetings.length} Excel meetings from meetings collection`);
                return;
              }
            }
          } catch (err) {
            console.log('Could not load from meetings collection:', err);
          }
          
          // Try localStorage as last resort
          console.log('No Excel data from backend, checking localStorage...');
          const savedExcelData = localStorage.getItem('excel_meetings');
          if (savedExcelData) {
            const excelData = JSON.parse(savedExcelData);
            setExcelMeetings(excelData);
            
            const savedStages = JSON.parse(localStorage.getItem('meeting_pipeline_stages') || '{}');
            const meetingsWithStages = excelData.map(m => ({
              ...m,
              pipeline_stage: savedStages[m.id] || 'prospecting'
            }));
            setMeetings(meetingsWithStages);
            console.log('✅ Loaded Excel data from localStorage');
          } else {
            // No Excel data at all - show empty with message
            setMeetings([]);
            console.log('No Excel data found - page will show empty');
          }
        }
      } catch (error) {
        console.error('Error loading Excel data:', error);
        setMeetings([]);
      } finally {
        setLoading(false);
      }
    };
    
    // Load personal meetings from backend API
    const loadPersonalMeetings = async () => {
      try {
        setLoading(true);
        const response = await apiCall('/meetings/');
        if (response && Array.isArray(response)) {
          // Transform backend meetings to match UI format
          const transformedMeetings = response.map((meeting, index) => ({
            id: meeting._id || meeting.id || `personal-${index}`,
            title: meeting.title || 'Untitled Meeting',
            date: meeting.date || new Date().toISOString().split('T')[0],
            time: meeting.time || '09:00',
            duration: meeting.duration || '1 hour',
            participants: meeting.participants || [],
            status: meeting.status || 'scheduled',
            category: meeting.category || 'Personal',
            type: meeting.type || 'personal',
            location: meeting.location || '',
            notes: meeting.notes || '',
            pipeline_stage: meeting.pipeline_stage || 'prospecting',
            created_at: meeting.created_at || new Date().toISOString()
          }));
          setPersonalMeetings(transformedMeetings);
          setMeetings(transformedMeetings);
          console.log(`✅ Loaded ${transformedMeetings.length} personal meetings`);
        } else {
          setPersonalMeetings([]);
          setMeetings([]);
        }
      } catch (error) {
        console.error('Error loading personal meetings:', error);
        setPersonalMeetings([]);
        setMeetings([]);
      } finally {
        setLoading(false);
      }
    };
    
    // Set Excel as default immediately (before data loads)
    setDataSource('excel');
    localStorage.setItem('data_source', 'excel');
    
    // Then load Excel data by default
    loadExcelData();
    loadConversations();
    
    // Load saved transcription links from localStorage
    const savedTranscriptionLinks = localStorage.getItem('meeting_transcription_links');
    if (savedTranscriptionLinks) {
      try {
        setMeetingTranscriptions(JSON.parse(savedTranscriptionLinks));
      } catch (error) {
        console.error('Error loading transcription links:', error);
      }
    }
    
    // Check for URL query parameters (from Text Center)
    const searchParams = new URLSearchParams(window.location.search);
    const prefillParam = searchParams.get('prefill');
    
    if (prefillParam) {
      try {
        const prefillData = JSON.parse(decodeURIComponent(prefillParam));
        setFormData(prev => ({
          ...prev,
          title: prefillData.title || prev.title,
          date: prefillData.date || prev.date,
          time: prefillData.time || prev.time,
          duration: prefillData.duration || prev.duration,
          location: prefillData.location || prev.location,
          notes: prefillData.notes || prev.notes,
          transcript: prefillData.transcript || prev.transcript,
          participants: prefillData.participants || prev.participants
        }));
        setIsCreating(true);
        // Clear URL params
        window.history.replaceState({}, '', '/meetings');
      } catch (error) {
        console.error('Error parsing prefill data:', error);
      }
    }
    
    // Check for prefill data from navigation state (from Transcribe or Email Center)
    if (location.state?.prefillData) {
      const { title, date, time, notes, transcript, transcription_id, participants } = location.state.prefillData;
      setFormData(prev => ({
        ...prev,
        title: title || prev.title,
        date: date || prev.date,
        time: time || prev.time,
        notes: notes || prev.notes,
        transcript: transcript || prev.transcript,
        transcription_id: transcription_id || prev.transcription_id,
        participants: participants || prev.participants
      }));
      setIsCreating(true);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const loadMeetings = async () => {
    try {
      // Load from Smartsheet
      console.log('📅 Loading meetings from Smartsheet...');
      
      // Use the API service to ensure correct URL handling
      const data = await apiCall('/smartsheet/meetings');
      
      if (data.success && data.meetings) {
        console.log(`✅ Loaded ${data.count} meetings from Smartsheet`);
        
        // Transform Smartsheet data to match our UI format
        const transformedMeetings = data.meetings.map((meeting, index) => {
          // Parse date from Smartsheet (format: "1/30/23  10:00 a.m." or other formats)
          let meetingDate = meeting.cells['Date of Meeting / Scheduled'];
          let meetingTime = '10:00';
          let meetingHour = 10;
          
          if (meetingDate) {
            try {
              // Handle format like "1/30/23  10:00 a.m."
              if (typeof meetingDate === 'string') {
                // Split date and time
                const parts = meetingDate.trim().split(/\s+/);
                const datePart = parts[0]; // "1/30/23"
                const timePart = parts[1] || '10:00'; // "10:00"
                const ampm = parts[2] || 'a.m.'; // "a.m."
                
                // Parse date: "1/30/23" -> "2023-01-30"
                const [month, day, year] = datePart.split('/');
                const fullYear = year.length === 2 ? `20${year}` : year;
                meetingDate = `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                
                // Parse time and hour for status calculation
                const timeMatch = timePart.match(/(\d+):(\d+)/);
                if (timeMatch) {
                  meetingHour = parseInt(timeMatch[1]);
                  const minutes = timeMatch[2];
                  
                  // Convert to 24-hour format
                  if (ampm.toLowerCase().includes('p.m.') && meetingHour !== 12) {
                    meetingHour += 12;
                  } else if (ampm.toLowerCase().includes('a.m.') && meetingHour === 12) {
                    meetingHour = 0;
                  }
                  
                  meetingTime = `${meetingHour.toString().padStart(2, '0')}:${minutes}`;
                }
              } else if (typeof meetingDate === 'number') {
                // If it's an Excel serial number
                const excelEpoch = new Date(1900, 0, 1);
                const date = new Date(excelEpoch.getTime() + (meetingDate - 2) * 24 * 60 * 60 * 1000);
                meetingDate = date.toISOString().split('T')[0];
              }
            } catch (e) {
              console.error('Error parsing date:', e);
              meetingDate = new Date().toISOString().split('T')[0];
            }
          } else {
            meetingDate = new Date().toISOString().split('T')[0];
          }
          
          // Calculate meeting status based on date and time
          const calculateStatus = () => {
            const now = new Date();
            const meetingDateTime = new Date(`${meetingDate}T${meetingTime}:00`);
            const meetingEndTime = new Date(meetingDateTime.getTime() + (2 * 60 * 60 * 1000)); // 2 hours later
            
            if (now < meetingDateTime) {
              return 'upcoming';
            } else if (now >= meetingDateTime && now < meetingEndTime) {
              return 'in_progress';
            } else {
              return 'done';
            }
          };
          
          const contactName = meeting.cells['Contact Name'] || 'No participant';
          const organization = meeting.cells['Organization'] || '';
          const email = meeting.cells['Email'] || '';
          const meetingType = meeting.cells['Type of Meeting - In Person / Virtual'];
          const isVirtual = meetingType?.toLowerCase().includes('zoom') || 
                           meetingType?.toLowerCase().includes('virtual') ||
                           meetingType?.toLowerCase().includes('teams');
          
          // Generate avatar from name using UI Avatars service
          let imageUrl = null;
          if (contactName && contactName !== 'No participant') {
            imageUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(contactName)}&size=128&background=random&color=fff&bold=true`;
          }
          
          // Build participant object with image URL
          const participant = contactName !== 'No participant' ? {
            name: contactName,
            company: organization,
            email: email,
            image_url: imageUrl
          } : null;
          
          // Debug: Log first few meetings
          if (index < 3) {
            console.log(`Meeting ${index}:`, {
              contactName,
              email,
              org: organization,
              date: meetingDate,
              time: meetingTime,
              status: calculateStatus(),
              rawDate: meeting.cells['Date of Meeting / Scheduled']
            });
          }
          
          return {
            id: meeting.smartsheet_row_id,
            smartsheet_row_id: meeting.smartsheet_row_id,
            row_number: meeting.row_number,
            title: contactName,
            contact_name: contactName,
            email: email,
            organization: organization,
            date: meetingDate,
            time: meetingTime,
            duration: 120, // 2 hours
            meeting_type: isVirtual ? 'virtual' : 'in-person',
            location: meetingType || 'TBD',
            category: 'Investments',
            status: calculateStatus(),
            participants: participant ? [participant] : [],
            notes: '',
            meeting_link: isVirtual ? meetingType : '',
            agenda: `Meeting with ${contactName}${organization ? ` from ${organization}` : ''}`,
            from_smartsheet: true
          };
        });
        
        setSmartsheetMeetings(transformedMeetings);
        
        // Load pipeline stages from localStorage
        const savedStages = JSON.parse(localStorage.getItem('meeting_pipeline_stages') || '{}');
        const meetingsWithStages = transformedMeetings.map(m => ({
          ...m,
          pipeline_stage: savedStages[m.id] || 'prospecting'
        }));
        
        setMeetings(meetingsWithStages);
        setLastSync(new Date());
      } else {
        console.error('Failed to load Smartsheet meetings:', data);
        // Fallback to mock data
        console.log('📅 Using mockup meetings data:', realEstateMeetingsMockData.length);
        setMeetings(realEstateMeetingsMockData);
      }
    } catch (error) {
      console.error('Error loading meetings:', error);
      // Fallback to mock data
      console.log('📅 Using mockup meetings data:', realEstateMeetingsMockData.length);
      setMeetings(realEstateMeetingsMockData);
    } finally {
      setLoading(false);
    }
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingExcel(true);
    setExcelFile(file);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        console.log('📊 Excel data parsed:', jsonData.length, 'rows');
        
        // Transform Excel data to meeting format
        const transformedMeetings = jsonData.map((row, index) => {
          // Parse date from Excel (format: "1/15/2025" or Excel serial number)
          let meetingDate = row['Meeting Date'] || row['Date'];
          let meetingTime = row['Meeting Time (CST)'] || row['Meeting Time'] || '10:00';
          
          // Handle Excel date serial numbers
          if (typeof meetingDate === 'number') {
            const excelEpoch = new Date(1900, 0, 1);
            const date = new Date(excelEpoch.getTime() + (meetingDate - 2) * 24 * 60 * 60 * 1000);
            meetingDate = date.toISOString().split('T')[0];
          } else if (typeof meetingDate === 'string') {
            // Parse MM/DD/YYYY format
            const parts = meetingDate.split('/');
            if (parts.length === 3) {
              const [month, day, year] = parts;
              meetingDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            }
          }
          
          // Parse time (format: "10:00 AM CST" or "10:00")
          if (typeof meetingTime === 'string') {
            const timeMatch = meetingTime.match(/(\d+):(\d+)/);
            if (timeMatch) {
              let hour = parseInt(timeMatch[1]);
              const minutes = timeMatch[2];
              
              // Convert to 24-hour format if AM/PM present
              if (meetingTime.includes('PM') && hour !== 12) {
                hour += 12;
              } else if (meetingTime.includes('AM') && hour === 12) {
                hour = 0;
              }
              
              meetingTime = `${hour.toString().padStart(2, '0')}:${minutes}`;
            }
          }
          
          const contactName = row['Contact Name'] || 'No participant';
          const company = row['Company'] || '';
          const email = row['Email'] || '';
          const phone = row['Phone'] || '';
          const status = row['Status'] || 'Scheduled';
          const meetingType = row['Meeting Type'] || 'Virtual';
          const notes = row['Additional Notes'] || '';
          
          // Determine if virtual based on meeting type
          const isVirtual = meetingType.toLowerCase().includes('zoom') || 
                           meetingType.toLowerCase().includes('teams') ||
                           meetingType.toLowerCase().includes('virtual');
          
          // Generate avatar from name
          let imageUrl = null;
          if (contactName && contactName !== 'No participant') {
            imageUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(contactName)}&size=128&background=random&color=fff&bold=true`;
          }
          
          // Build participant object
          const participant = contactName !== 'No participant' ? {
            name: contactName,
            company: company,
            email: email,
            phone: phone,
            image_url: imageUrl
          } : null;
          
          // Calculate meeting status
          const calculateStatus = () => {
            const now = new Date();
            const meetingDateTime = new Date(`${meetingDate}T${meetingTime}:00`);
            const meetingEndTime = new Date(meetingDateTime.getTime() + (2 * 60 * 60 * 1000));
            
            if (now < meetingDateTime) {
              return 'upcoming';
            } else if (now >= meetingDateTime && now < meetingEndTime) {
              return 'in_progress';
            } else {
              return 'done';
            }
          };
          
          return {
            id: `excel-${index}-${Date.now()}`,
            title: `Meeting with ${contactName}`,
            date: meetingDate,
            time: meetingTime,
            duration: 60,
            meeting_type: isVirtual ? 'virtual' : 'in-person',
            category: 'Investments',
            notes: notes,
            agenda: notes || `Meeting scheduled with ${contactName} from ${company}`,
            organization: company,
            location: isVirtual ? 'Virtual' : company,
            status: calculateStatus(),
            excel_status: status,
            participants: participant ? [participant] : [],
            pipeline_stage: 'prospecting'
          };
        });
        
        console.log('✅ Transformed', transformedMeetings.length, 'Excel meetings');
        setExcelMeetings(transformedMeetings);
        
        // Save to localStorage
        localStorage.setItem('excel_meetings', JSON.stringify(transformedMeetings));
        localStorage.setItem('excel_file_name', file.name);
        localStorage.setItem('excel_upload_date', new Date().toISOString());
        localStorage.setItem('data_source', 'excel'); // Save data source preference
        
        // Switch to Excel data source
        setDataSource('excel');
        
        // Apply saved pipeline stages
        const savedStages = JSON.parse(localStorage.getItem('meeting_pipeline_stages') || '{}');
        const meetingsWithStages = transformedMeetings.map(m => ({
          ...m,
          pipeline_stage: savedStages[m.id] || 'prospecting'
        }));
        
        setMeetings(meetingsWithStages);
        
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        alert('Error parsing Excel file. Please check the file format.');
      } finally {
        setUploadingExcel(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const syncWithSmartsheet = async () => {
    setSyncing(true);
    try {
      console.log('🔄 Syncing with Smartsheet...');
      await loadMeetings();
      console.log('✅ Sync complete');
    } catch (error) {
      console.error('❌ Sync failed:', error);
    } finally {
      setSyncing(false);
    }
  };

  const loadConversations = async () => {
    try {
      // Load real conversations from API
      console.log('📋 Loading conversations from API...');
      const response = await apiCall('/conversations-transcripts/');
      
      // Handle both array response and {conversations: [...]} response
      let conversationsArray = [];
      if (response && Array.isArray(response)) {
        conversationsArray = response;
      } else if (response && response.conversations && Array.isArray(response.conversations)) {
        conversationsArray = response.conversations;
      }
      
      if (conversationsArray.length > 0) {
        // Sort by created_at descending (newest first)
        const sortedConversations = conversationsArray.sort((a, b) => {
          const dateA = new Date(a.created_at || 0);
          const dateB = new Date(b.created_at || 0);
          return dateB - dateA; // Newest first
        });
        
        setConversations(sortedConversations);
        console.log(`✅ Loaded ${sortedConversations.length} conversations`);
      } else {
        // Fallback to mock data if API fails or returns empty
        console.log('📋 Using mockup conversations data as fallback');
        setConversations(transcribeConversationsMockData);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
      // Fallback to mock data
      setConversations(transcribeConversationsMockData);
    }
  };

  // Delete a conversation
  const handleDeleteConversation = async (conversationId) => {
    try {
      console.log('🗑️ Deleting conversation:', conversationId);
      
      const response = await apiCall(`/conversations-transcripts/${conversationId}`, {
        method: 'DELETE'
      });
      
      if (response) {
        // Remove from local state
        setConversations(prev => prev.filter(c => c.id !== conversationId));
        
        // Remove any meeting links to this conversation
        const updatedLinks = { ...meetingTranscriptions };
        Object.keys(updatedLinks).forEach(key => {
          if (updatedLinks[key] === conversationId) {
            delete updatedLinks[key];
          }
        });
        setMeetingTranscriptions(updatedLinks);
        localStorage.setItem('meeting_transcription_links', JSON.stringify(updatedLinks));
        
        console.log('✅ Conversation deleted successfully');
      }
    } catch (error) {
      console.error('❌ Error deleting conversation:', error);
      alert('Failed to delete conversation. Please try again.');
    }
  };

  // Timer for recording
  useEffect(() => {
    if (isRecording) {
      timerIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    }
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [isRecording]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      // Create audio context for visualization
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const audioSource = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      audioSource.connect(analyser);
      analyserRef.current = analyser;

      // Start visualizing audio levels
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const updateWaveform = () => {
        if (!analyserRef.current) return;
        
        analyser.getByteFrequencyData(dataArray);
        
        // Sample 12 points across the frequency data for our 12 bars
        const bars = [];
        const samplesPerBar = Math.floor(bufferLength / 12);
        
        for (let i = 0; i < 12; i++) {
          const start = i * samplesPerBar;
          const end = start + samplesPerBar;
          let sum = 0;
          for (let j = start; j < end; j++) {
            sum += dataArray[j];
          }
          const average = sum / samplesPerBar;
          // Normalize to 0-100 range
          bars.push(Math.min(100, (average / 255) * 100));
        }
        
        setAudioLevels(bars);
        animationFrameRef.current = requestAnimationFrame(updateWaveform);
      };

      updateWaveform();

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
        
        // Stop visualization
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        if (audioContext) {
          audioContext.close();
        }
        analyserRef.current = null;
        setAudioLevels(Array(12).fill(0));
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      setTranscriptionError(null);
    } catch (err) {
      console.error('Error starting recording:', err);
      setTranscriptionError('Failed to access microphone. Please grant permission.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeRecording = async () => {
    if (!audioBlob) return;

    setTranscribing(true);
    setTranscriptionError(null);

    try {
      // Step 1: Convert audio blob to file and transcribe using Whisper
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      
      console.log('🎤 Sending audio for transcription...');
      
      const transcribeResponse = await fetch(`${apiUrl}/api/whisper/transcribe`, {
        method: 'POST',
        body: formData
      });
      
      if (!transcribeResponse.ok) {
        throw new Error('Failed to transcribe audio');
      }
      
      const transcribeResult = await transcribeResponse.json();
      console.log('✅ Transcription complete:', transcribeResult);
      
      // Step 2: Convert audio blob to base64 for storage
      const reader = new FileReader();
      const audioBase64 = await new Promise((resolve, reject) => {
        reader.onloadend = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(audioBlob);
      });
      
      // Step 3: Save to conversations-transcripts with the transcript
      const savePayload = {
        audio_data: audioBase64,
        transcript: transcribeResult.text || transcribeResult.transcript || '',
        duration: recordingTime,
        language: transcribeResult.language || 'en'
      };
      
      console.log('💾 Saving conversation...');
      
      const saveResponse = await fetch(`${apiUrl}/api/conversations-transcripts/?quick_save=true`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(savePayload)
      });
      
      if (!saveResponse.ok) {
        throw new Error('Failed to save conversation');
      }
      
      const savedConversation = await saveResponse.json();
      console.log('✅ Conversation saved:', savedConversation);
      
      // Step 4: Add the new conversation directly to state (at the top)
      if (savedConversation && savedConversation.id) {
        setConversations(prev => [savedConversation, ...prev]);
        console.log('✅ Added new conversation to list');
      }
      
      // Also reload from API to ensure sync
      await loadConversations();
      
      // Reset states
      setAudioBlob(null);
      setRecordingTime(0);
      setTranscribing(false);
      
      // Show success message
      alert(`Recording saved successfully! "${savedConversation.title}"`);
      
    } catch (err) {
      console.error('Error processing recording:', err);
      setTranscriptionError(err.message || 'Failed to process recording');
      setTranscribing(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (25MB max)
    if (file.size > 25 * 1024 * 1024) {
      setTranscriptionError('File size exceeds 25MB limit');
      return;
    }

    // Validate file format
    const fileExt = file.name.split('.').pop().toLowerCase();
    if (!supportedFormats.includes(fileExt)) {
      setTranscriptionError(`Unsupported format. Please use: ${supportedFormats.join(', ')}`);
      return;
    }

    setSelectedFile(file);
    setTranscriptionError(null);
    setTranscription(null);
  };

  const handleTranscribe = async () => {
    if (!selectedFile) {
      setTranscriptionError('Please select an audio file');
      return;
    }

    setTranscribing(true);
    setTranscriptionError(null);

    try {
      // For now, just show success
      // In production, this would call the API
      alert('File uploaded! (In production, this would transcribe via API)');
      
      // Reload conversations
      await loadConversations();
      
      // Reset
      setSelectedFile(null);
      setTranscribing(false);
    } catch (err) {
      console.error('Transcription error:', err);
      setTranscriptionError(err.message || 'Failed to transcribe audio');
      setTranscribing(false);
    }
  };

  const getMeetingStatus = (meeting) => {
    const meetingDateTime = new Date(`${meeting.date}T${meeting.time}`);
    const now = new Date();
    const meetingEnd = new Date(meetingDateTime.getTime() + (meeting.duration || 60) * 60000);

    if (now < meetingDateTime) {
      return 'upcoming';
    } else if (now >= meetingDateTime && now <= meetingEnd) {
      return 'in_progress';
    } else {
      return 'done';
    }
  };

  const parseTranscriptToMessages = (transcript) => {
    // Split by speaker pattern (e.g., "Darren:", "Person 2:")
    const lines = transcript.split('\n').filter(line => line.trim());
    const messages = [];
    
    lines.forEach(line => {
      const match = line.match(/^(.+?):\s*(.+)$/);
      if (match) {
        messages.push({
          speaker: match[1].trim(),
          text: match[2].trim()
        });
      } else if (messages.length > 0) {
        // Continue previous message
        messages[messages.length - 1].text += ' ' + line.trim();
      }
    });
    
    return messages;
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins}m ${secs}s`;
    } else if (mins > 0) {
      return `${mins}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let meetingId;
      if (isEditing && editingMeetingId) {
        await apiCall(`/meetings/${editingMeetingId}`, {
          method: 'PUT',
          body: JSON.stringify(formData)
        });
        meetingId = editingMeetingId;
      } else {
        const response = await apiCall('/meetings/', {
          method: 'POST',
          body: JSON.stringify(formData)
        });
        meetingId = response.id;
        
        // If this meeting was created from a transcription, link it back
        if (formData.transcription_id) {
          try {
            await apiCall(`/conversations-transcripts/${formData.transcription_id}`, {
              method: 'PATCH',
              body: JSON.stringify({ meeting_id: meetingId })
            });
            console.log('✅ Linked meeting to conversation');
          } catch (linkError) {
            console.error('Failed to link meeting to conversation:', linkError);
            // Don't fail the whole operation if linking fails
          }
        }
      }
      await loadMeetings();
      resetForm();
    } catch (error) {
      console.error('Error saving meeting:', error);
      alert('Failed to save meeting');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this meeting?')) return;
    
    try {
      await apiCall(`/meetings/${id}`, { method: 'DELETE' });
      await loadMeetings();
    } catch (error) {
      console.error('Error deleting meeting:', error);
      alert('Failed to delete meeting');
    }
  };

  // Handle delete for both Excel and regular meetings
  const handleDeleteMeeting = async (meeting) => {
    try {
      // Check if it's an Excel meeting
      if (meeting.source === 'desktop_excel' || meeting.excel_meeting_id) {
        // Delete from Excel backend
        const response = await apiCall(`/excel/delete-meeting/${meeting.id}`, { method: 'DELETE' });
        if (response) {
          // Remove from local state
          setMeetings(prev => prev.filter(m => m.id !== meeting.id));
          setExcelMeetings(prev => prev.filter(m => m.id !== meeting.id));
          console.log(`✅ Deleted Excel meeting: ${meeting.title || meeting.organization}`);
        }
      } else {
        // Delete from regular meetings API
        await apiCall(`/meetings/${meeting.id}`, { method: 'DELETE' });
        await loadMeetings();
      }
    } catch (error) {
      console.error('Error deleting meeting:', error);
      alert('Failed to delete meeting. Please try again.');
    }
  };

  const handleEdit = (meeting) => {
    setFormData({
      title: meeting.title,
      date: meeting.date,
      time: meeting.time,
      duration: meeting.duration || 60,
      meeting_link: meeting.meeting_link || '',
      location: meeting.location || '',
      meeting_type: meeting.meeting_type || (meeting.meeting_link ? 'virtual' : 'in-person'),
      category: meeting.category || 'Personal',
      notes: meeting.notes || '',
      agenda: meeting.agenda || '',
      transcript: meeting.transcript || '',
      transcription_id: meeting.transcription_id || '',
      participants: meeting.participants || []
    });
    setEditingMeetingId(meeting.id);
    setIsEditing(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      date: '',
      time: '',
      duration: 60,
      meeting_link: '',
      location: '',
      meeting_type: 'virtual',
      category: 'Personal',
      notes: '',
      agenda: '',
      transcript: '',
      transcription_id: '',
      participants: []
    });
    setNewParticipant({
      name: '',
      email: '',
      company: '',
      role: '',
      phone: '',
      image_url: '',
      image_file: null
    });
    setIsCreating(false);
    setIsEditing(false);
    setEditingMeetingId(null);
  };

  const handleImageUpload = async (file) => {
    if (!file) return null;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const data = await apiUpload('/uploads/thumbnail', formData);
      console.log('✅ Image uploaded successfully:', data);
      return data.url;
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image: ' + error.message);
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  // Document upload handler for meetings
  const handleDocumentUpload = async (file, meetingId) => {
    if (!file || !meetingId) return null;
    
    // Validate file format
    const fileExt = file.name.split('.').pop().toLowerCase();
    if (!supportedDocFormats.includes(fileExt)) {
      alert(`Unsupported file format. Supported: ${supportedDocFormats.join(', ')}`);
      return null;
    }
    
    // Validate file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      alert('File size must be less than 50MB');
      return null;
    }
    
    setUploadingDocument(true);
    setDocumentUploadMeetingId(meetingId);
    
    try {
      // Step 1: Upload file to storage
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadResponse = await apiUpload('/uploads/documents', formData);
      console.log('✅ Document uploaded to storage:', uploadResponse);
      
      // Step 2: Add document reference to meeting
      const docData = {
        filename: uploadResponse.filename,
        original_filename: uploadResponse.original_filename,
        url: uploadResponse.url,
        content_type: uploadResponse.content_type,
        size: uploadResponse.size
      };
      
      const response = await apiCall(`/meetings/${meetingId}/documents`, {
        method: 'POST',
        body: JSON.stringify(docData)
      });
      
      console.log('✅ Document linked to meeting:', response);
      
      // Update local state
      if (selectedMeeting && selectedMeeting.id === meetingId) {
        const updatedDocs = [...(selectedMeeting.documents || []), response.document];
        setSelectedMeeting({ ...selectedMeeting, documents: updatedDocs });
      }
      
      // Update meetings list
      setMeetings(prevMeetings => prevMeetings.map(m => {
        if (m.id === meetingId) {
          return { ...m, documents: [...(m.documents || []), response.document] };
        }
        return m;
      }));
      
      return response.document;
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Failed to upload document: ' + error.message);
      return null;
    } finally {
      setUploadingDocument(false);
      setDocumentUploadMeetingId(null);
    }
  };

  // Remove document from meeting
  const handleRemoveDocument = async (meetingId, documentId) => {
    if (!window.confirm('Are you sure you want to remove this document?')) return;
    
    try {
      await apiCall(`/meetings/${meetingId}/documents/${documentId}`, {
        method: 'DELETE'
      });
      
      console.log('✅ Document removed from meeting');
      
      // Update local state
      if (selectedMeeting && selectedMeeting.id === meetingId) {
        const updatedDocs = (selectedMeeting.documents || []).filter(d => d.id !== documentId);
        setSelectedMeeting({ ...selectedMeeting, documents: updatedDocs });
      }
      
      // Update meetings list
      setMeetings(prevMeetings => prevMeetings.map(m => {
        if (m.id === meetingId) {
          return { ...m, documents: (m.documents || []).filter(d => d.id !== documentId) };
        }
        return m;
      }));
    } catch (error) {
      console.error('Error removing document:', error);
      alert('Failed to remove document: ' + error.message);
    }
  };

  // Get file icon based on content type
  const getDocumentIcon = (contentType, filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    if (contentType?.includes('pdf') || ext === 'pdf') return '📄';
    if (contentType?.includes('word') || ['doc', 'docx'].includes(ext)) return '📝';
    if (contentType?.includes('excel') || contentType?.includes('spreadsheet') || ['xls', 'xlsx'].includes(ext)) return '📊';
    if (contentType?.includes('powerpoint') || contentType?.includes('presentation') || ['ppt', 'pptx'].includes(ext)) return '📽️';
    if (contentType?.includes('image') || ['png', 'jpg', 'jpeg', 'gif'].includes(ext)) return '🖼️';
    if (contentType?.includes('text') || ext === 'txt') return '📃';
    return '📎';
  };

  const addParticipant = async () => {
    if (newParticipant.name.trim()) {
      let imageUrl = newParticipant.image_url;
      
      // If a file was selected, upload it first
      if (newParticipant.image_file) {
        imageUrl = await handleImageUpload(newParticipant.image_file);
      }
      
      setFormData({
        ...formData,
        participants: [...formData.participants, { 
          name: newParticipant.name,
          email: newParticipant.email,
          company: newParticipant.company,
          role: newParticipant.role,
          phone: newParticipant.phone,
          image_url: imageUrl || ''
        }]
      });
      setNewParticipant({
        name: '',
        email: '',
        company: '',
        role: '',
        phone: '',
        image_url: '',
        image_file: null
      });
    }
  };

  const removeParticipant = (index) => {
    setFormData({
      ...formData,
      participants: formData.participants.filter((_, i) => i !== index)
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'TBD';
      const formatted = date.toLocaleDateString('en-US', { 
        timeZone: 'America/Chicago',
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        weekday: 'short'
      });
      return formatted === 'Invalid Date' ? 'TBD' : formatted;
    } catch {
      return 'TBD';
    }
  };

  const formatTimeCST = (timeString) => {
    if (!timeString) return '';
    // If it's a full datetime string
    if (timeString.includes('T') || timeString.includes('-')) {
      const date = new Date(timeString);
      return date.toLocaleTimeString('en-US', {
        timeZone: 'America/Chicago',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
    // If it's just a time string like "14:30"
    const [hours, minutes] = timeString.split(':');
    let hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12;
    return `${hour}:${minutes} ${ampm}`;
  };

  const getMeetingLinkType = (link) => {
    if (!link) return null;
    if (link.includes('zoom.us')) return 'Zoom';
    if (link.includes('teams.microsoft.com') || link.includes('teams.live.com')) return 'Teams';
    return 'Meeting';
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const filteredMeetings = meetings.filter(meeting => {
    const matchesStatus = filterStatus === 'all' || getMeetingDisplayStatus(meeting) === filterStatus;
    const matchesCategory = selectedCategory === 'all' || (meeting.category || 'Personal').toLowerCase() === selectedCategory.toLowerCase();
    return matchesStatus && matchesCategory;
  }).sort((a, b) => {
    let comparison = 0;
    
    if (sortField === 'date') {
      // Check if dates are TBD or invalid
      const isTBD_A = !a.date || a.date === 'TBD' || a.date === '-' || a.date === '';
      const isTBD_B = !b.date || b.date === 'TBD' || b.date === '-' || b.date === '';
      
      // TBD meetings always go to the bottom
      if (isTBD_A && !isTBD_B) return 1;  // A is TBD, goes after B
      if (!isTBD_A && isTBD_B) return -1; // B is TBD, goes after A
      if (isTBD_A && isTBD_B) return 0;   // Both TBD, keep original order
      
      // Parse dates properly - handle "2025-11-06 00:00:00" format
      const cleanDateA = (a.date || '').split(' ')[0];
      const cleanDateB = (b.date || '').split(' ')[0];
      
      // Parse Excel decimal time format
      const parseTime = (timeVal) => {
        if (!timeVal || timeVal === 'TBD' || timeVal === '-') return '00:00';
        if (typeof timeVal === 'number' || !isNaN(parseFloat(timeVal))) {
          const decimal = parseFloat(timeVal);
          const hours = Math.floor(decimal * 24);
          const minutes = Math.round((decimal * 24 - hours) * 60);
          return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }
        return timeVal;
      };
      
      const timeStrA = parseTime(a.time);
      const timeStrB = parseTime(b.time);
      
      const dateA = new Date(`${cleanDateA}T${timeStrA}`);
      const dateB = new Date(`${cleanDateB}T${timeStrB}`);
      
      // Handle invalid dates
      const timeA = isNaN(dateA.getTime()) ? 0 : dateA.getTime();
      const timeB = isNaN(dateB.getTime()) ? 0 : dateB.getTime();
      
      comparison = timeA - timeB;
    } else if (sortField === 'name') {
      const nameA = a.participants[0]?.name || a.title || '';
      const nameB = b.participants[0]?.name || b.title || '';
      comparison = nameA.localeCompare(nameB);
    }
    
    // desc = newest first (larger dates first), asc = oldest first
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-white">Loading meetings...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-3 md:p-6">
      <div className="max-w-[95%] mx-auto px-2 md:px-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 md:mb-6 gap-3">
          <div className="flex items-center gap-2 md:gap-3">
            <Calendar className="text-white" size={24} />
            <div>
              <h1 className="text-xl md:text-3xl font-bold text-white">Meetings</h1>
              {lastSync && (
                <p className="text-xs md:text-sm text-gray-400 mt-1">
                  Last synced: {lastSync.toLocaleTimeString()}
                </p>
              )}
            </div>
          </div>
          
          {/* Refresh Button */}
          <button
            onClick={async () => {
              setSyncing(true);
              try {
                const response = await apiCall('/excel/get-desktop-data');
                if (response.meetings && response.meetings.length > 0) {
                  setExcelMeetings(response.meetings);
                  const savedStages = JSON.parse(localStorage.getItem('meeting_pipeline_stages') || '{}');
                  const meetingsWithStages = response.meetings.map(m => ({
                    ...m,
                    pipeline_stage: savedStages[m.id] || m.pipeline_stage || 'prospecting'
                  }));
                  setMeetings(meetingsWithStages);
                  setLastSync(new Date());
                  console.log(`✅ Refreshed ${response.meetings.length} meetings`);
                } else {
                  alert('No Excel data found. Run the sync script on your Mac first.');
                }
              } catch (error) {
                console.error('Error refreshing:', error);
                alert('Error refreshing data. Please try again.');
              } finally {
                setSyncing(false);
              }
            }}
            disabled={syncing}
            className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-colors disabled:opacity-50 text-sm md:text-base"
            data-testid="refresh-excel-btn"
          >
            <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">{syncing ? 'Refreshing...' : 'Refresh Data'}</span>
            <span className="sm:hidden">{syncing ? '...' : 'Refresh'}</span>
          </button>
        </div>

        {/* Filter Buttons */}
        <div className="bg-black border border-gray-400 rounded-xl p-3 md:p-4 mb-4 md:mb-6 space-y-3 md:space-y-4 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
          {/* Category Row - Category filters on left, Action buttons on right */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            {/* Category Filters */}
            <div className="flex-1">
              <label className="block text-xs md:text-sm font-medium text-white mb-1.5 md:mb-2">Category</label>
              <div className="flex gap-1.5 md:gap-2 flex-wrap items-center">
                {customMeetingCategories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id === 'all' ? 'all' : cat.name)}
                    data-testid={`meeting-category-${cat.id}`}
                    className={`px-2 md:px-4 py-1 md:py-2 rounded-lg text-xs md:text-sm transition-colors flex items-center gap-1.5 ${
                      selectedCategory === (cat.id === 'all' ? 'all' : cat.name)
                        ? 'bg-white text-black'
                        : 'bg-white/5 text-white hover:bg-white/10'
                    }`}
                  >
                    <span>{cat.icon}</span>
                    {cat.name}
                  </button>
                ))}
                
                {/* Edit Categories Button */}
                <button
                  onClick={() => setShowMeetingCategoryModal(true)}
                  data-testid="edit-meeting-categories-btn"
                  className="px-2 py-1 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all whitespace-nowrap bg-transparent border border-dashed border-gray-500 text-gray-400 hover:bg-gray-800 hover:text-white flex items-center gap-1"
                  title="Edit Categories"
                >
                  <Pencil size={12} />
                  <span className="hidden md:inline">Edit</span>
                </button>
              </div>
            </div>
            
            {/* Action Buttons - Right side */}
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {/* View Toggle */}
              <div className="flex items-center gap-1 md:gap-2 bg-black border border-gray-600 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('kanban')}
                  className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg transition-colors text-sm md:text-base ${
                    viewMode === 'kanban'
                      ? 'bg-white text-black'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  Grid
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg transition-colors text-sm md:text-base ${
                    viewMode === 'list'
                      ? 'bg-white text-black'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  List
                </button>
              </div>
              
              {/* Data Source Toggle */}
              <div className="flex items-center gap-1 md:gap-2 bg-black border border-white/40 rounded-lg p-1 overflow-x-auto">
                <button
                  onClick={async () => {
                    setDataSource('excel');
                    localStorage.setItem('data_source', 'excel');
                    
                    // Fetch Excel data from backend
                    try {
                      const response = await apiCall('/excel/get-desktop-data');
                      if (response.meetings && response.meetings.length > 0) {
                        setExcelMeetings(response.meetings);
                        
                        // Apply saved pipeline stages
                        const savedStages = JSON.parse(localStorage.getItem('meeting_pipeline_stages') || '{}');
                        const meetingsWithStages = response.meetings.map(m => ({
                          ...m,
                          pipeline_stage: savedStages[m.id] || 'prospecting'
                        }));
                        setMeetings(meetingsWithStages);
                      } else {
                        alert('No Excel data found. Please run the sync script first.');
                      }
                    } catch (error) {
                      console.error('Error fetching Excel data:', error);
                      alert('Error loading Excel data. Please try again.');
                    }
                  }}
                  className={`px-2 md:px-3 py-1 md:py-1.5 rounded text-xs md:text-sm font-medium transition-colors whitespace-nowrap ${
                    dataSource === 'excel'
                      ? 'bg-green-600 text-white'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  Excel
                </button>
                <button
                  onClick={async () => {
                    setDataSource('personal');
                    localStorage.setItem('data_source', 'personal');
                    
                    // Fetch Personal meetings from backend
                    try {
                      setLoading(true);
                      const response = await apiCall('/meetings/');
                    if (response && Array.isArray(response)) {
                      const transformedMeetings = response.map((meeting, index) => ({
                        id: meeting._id || meeting.id || `personal-${index}`,
                        title: meeting.title || 'Untitled Meeting',
                        date: meeting.date || new Date().toISOString().split('T')[0],
                        time: meeting.time || '09:00',
                        duration: meeting.duration || '1 hour',
                        participants: meeting.participants || [],
                        status: meeting.status || 'scheduled',
                        category: meeting.category || 'Personal',
                        type: meeting.type || 'personal',
                        location: meeting.location || '',
                        notes: meeting.notes || '',
                        pipeline_stage: meeting.pipeline_stage || 'prospecting',
                        created_at: meeting.created_at || new Date().toISOString()
                      }));
                      setPersonalMeetings(transformedMeetings);
                      setMeetings(transformedMeetings);
                    } else {
                      setPersonalMeetings([]);
                      setMeetings([]);
                    }
                  } catch (error) {
                    console.error('Error fetching personal meetings:', error);
                  } finally {
                    setLoading(false);
                  }
                }}
                className={`px-2 md:px-3 py-1 md:py-1.5 rounded text-xs md:text-sm font-medium transition-colors whitespace-nowrap ${
                  dataSource === 'personal'
                    ? 'bg-purple-600 text-white'
                    : 'text-white/60 hover:text-white'
                }`}
              >
                Personal
              </button>
              <button
                onClick={() => {
                  setDataSource('smartsheet');
                  localStorage.setItem('data_source', 'smartsheet');
                  loadMeetings();
                }}
                className={`px-2 md:px-3 py-1 md:py-1.5 rounded text-xs md:text-sm font-medium transition-colors whitespace-nowrap ${
                  dataSource === 'smartsheet'
                    ? 'bg-blue-600 text-white'
                    : 'text-white/60 hover:text-white'
                }`}
              >
                Smartsheet
              </button>
            </div>
            
              {/* Smartsheet Sync Button */}
              {dataSource === 'smartsheet' && (
                <button
                  onClick={syncWithSmartsheet}
                  disabled={syncing}
                  className="flex items-center gap-1.5 md:gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-2 md:px-3 py-1.5 md:py-2 rounded-lg transition-colors text-xs md:text-sm"
                >
                  <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
                  {syncing ? 'Syncing...' : 'Sync'}
                </button>
              )}
              
              {/* Personal Refresh Button */}
              {dataSource === 'personal' && (
                <button
                  onClick={async () => {
                    try {
                      setLoading(true);
                      const response = await apiCall('/meetings/');
                      if (response && Array.isArray(response)) {
                        const transformedMeetings = response.map((meeting, index) => ({
                          id: meeting._id || meeting.id || `personal-${index}`,
                          title: meeting.title || 'Untitled Meeting',
                          date: meeting.date || new Date().toISOString().split('T')[0],
                          time: meeting.time || '09:00',
                          duration: meeting.duration || '1 hour',
                          participants: meeting.participants || [],
                          status: meeting.status || 'scheduled',
                          category: meeting.category || 'Personal',
                          type: meeting.type || 'personal',
                          location: meeting.location || '',
                          notes: meeting.notes || '',
                          pipeline_stage: meeting.pipeline_stage || 'prospecting',
                          created_at: meeting.created_at || new Date().toISOString()
                        }));
                        setPersonalMeetings(transformedMeetings);
                        setMeetings(transformedMeetings);
                      }
                    } catch (error) {
                      console.error('Error refreshing personal meetings:', error);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  className="flex items-center gap-1.5 md:gap-2 bg-purple-600 hover:bg-purple-700 text-white px-2 md:px-3 py-1.5 md:py-2 rounded-lg transition-colors text-xs md:text-sm"
                >
                  <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                  Refresh
                </button>
              )}
              
              {/* Excel Upload Button */}
              {dataSource === 'excel' && (
                <label className="flex items-center gap-1.5 md:gap-2 bg-green-600 hover:bg-green-700 text-white px-2 md:px-3 py-1.5 md:py-2 rounded-lg transition-colors cursor-pointer text-xs md:text-sm">
                  <Upload size={16} className={uploadingExcel ? 'animate-pulse' : ''} />
                  {uploadingExcel ? 'Uploading...' : 'Upload'}
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleExcelUpload}
                    className="hidden"
                    disabled={uploadingExcel}
                  />
                </label>
              )}
              
              {/* New Meeting Button */}
              <button
                onClick={() => setIsCreating(true)}
                className="flex items-center gap-1.5 md:gap-2 bg-white hover:bg-gray-100 text-black px-2 md:px-3 py-1.5 md:py-2 rounded-lg transition-colors text-xs md:text-sm font-medium"
              >
                <Plus size={16} />
                <span className="hidden sm:inline">New Meeting</span>
                <span className="sm:hidden">New</span>
              </button>
            </div>
          </div>
        </div>

        {/* Kanban Board View */}
        {viewMode === 'kanban' && (
          <div className="flex gap-3 overflow-x-auto pb-4">
            {/* Kanban Columns */}
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 min-w-0">
            {pipelineStages.map(stage => {
              // Filter meetings for this stage - use auto-calculated stage if no manual override
              const stageMeetings = filteredMeetings.filter(m => {
                // Check if there's a manually set status first
                const manualStatus = meetingStatuses[m.id];
                if (manualStatus) {
                  return manualStatus === stage.id;
                }
                // Otherwise use auto-calculated stage based on date
                const autoStage = calculatePipelineStage(m);
                return autoStage === stage.id;
              });
              
              // Sort meetings: Newest dates first → Older dates → TBD at bottom
              const sortedStageMeetings = stageMeetings.sort((a, b) => {
                // Helper to check if date is valid (not TBD, -, or empty)
                const isValidDate = (dateStr) => {
                  if (!dateStr || dateStr === 'TBD' || dateStr === '-' || dateStr === '') return false;
                  // Handle "2025-11-06 00:00:00" format
                  const cleanDate = String(dateStr).split(' ')[0];
                  const date = new Date(cleanDate);
                  return !isNaN(date.getTime());
                };
                
                const aHasDate = isValidDate(a.date);
                const bHasDate = isValidDate(b.date);
                
                // TBD meetings always go to the bottom
                if (!aHasDate && bHasDate) return 1;  // A is TBD, goes after B
                if (aHasDate && !bHasDate) return -1; // B is TBD, goes after A
                if (!aHasDate && !bHasDate) return 0; // Both TBD, keep order
                
                // Both have valid dates - sort newest first (descending)
                const cleanDateA = String(a.date).split(' ')[0];
                const cleanDateB = String(b.date).split(' ')[0];
                const aDate = new Date(cleanDateA);
                const bDate = new Date(cleanDateB);
                return bDate - aDate; // Descending: newest first
              });
              
              // Group meetings by organization
              const groupedMeetings = sortedStageMeetings.reduce((acc, meeting) => {
                const orgName = meeting.organization || meeting.title || 'Unknown';
                if (!acc[orgName]) {
                  acc[orgName] = [];
                }
                acc[orgName].push(meeting);
                return acc;
              }, {});
              
              const dealCards = Object.entries(groupedMeetings).map(([orgName, meetings]) => {
                const totalValue = meetings.length * 12.5;
                const allParticipants = meetings.flatMap(m => m.participants || []);
                const uniqueParticipants = Array.from(new Set(allParticipants.map(p => p.name)))
                  .map(name => allParticipants.find(p => p.name === name));
                
                return {
                  organization: orgName,
                  meetings: meetings,
                  totalValue: totalValue,
                  participants: uniqueParticipants,
                  location: meetings[0].location,
                  pipeline_stage: meetings[0].pipeline_stage
                };
              });
              
              const stageValue = dealCards.reduce((sum, deal) => sum + deal.totalValue, 0);
              
              return (
                <div
                  key={stage.id}
                  className="flex flex-col"
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, stage.id)}
                >
                  {/* Stage Header */}
                  <div className="bg-black border border-white/20 rounded-t-xl p-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-white font-bold text-sm">{stage.name}</h3>
                      <span className="text-white text-xs font-semibold">{dealCards.length}</span>
                    </div>
                  </div>
                  
                  {/* Cards Container */}
                  <div className="bg-black border border-white/20 border-t-0 rounded-b-xl p-2 min-h-[800px] max-h-[800px] overflow-y-auto space-y-2">
                    {dealCards.map((deal, idx) => {
                      const primaryParticipant = deal.participants[0];
                      const oldestMeeting = deal.meetings.sort((a, b) => new Date(a.date) - new Date(b.date))[0];
                      const meetingDate = new Date(oldestMeeting.date);
                      const daysAgo = Math.floor((new Date() - meetingDate) / (1000 * 60 * 60 * 24));
                      
                      // Calculate win percentage based on pipeline stage
                      const winPercentages = {
                        'tbd': 25,
                        'upcoming': 50,
                        'in_progress': 75,
                        'done': 100
                      };
                      const currentStage = getMeetingDisplayStatus(deal.meetings[0]);
                      const winPercentage = winPercentages[currentStage] || 25;
                      
                      return (
                        <div
                          key={`${deal.organization}-${idx}`}
                          draggable
                          onDragStart={(e) => {
                            // Always set the meeting being dragged
                            handleDragStart(e, deal.meetings[0]);
                            setDraggedCardIndex(idx);
                            setDraggedColumnId(stage.id);
                          }}
                          onDragEnd={(e) => {
                            handleDragEnd(e);
                            setDraggedCardIndex(null);
                            setDraggedColumnId(null);
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            
                            // Show indicator based on what's being dragged
                            if (draggedTranscription) {
                              e.currentTarget.classList.add('ring-2', 'ring-cyan-500');
                            } else if (draggedMeeting) {
                              e.currentTarget.classList.add('ring-2', 'ring-yellow-500');
                            }
                          }}
                          onDragLeave={(e) => {
                            e.currentTarget.classList.remove('ring-2', 'ring-cyan-500', 'ring-yellow-500');
                          }}
                          onDrop={(e) => {
                            e.currentTarget.classList.remove('ring-2', 'ring-cyan-500', 'ring-yellow-500');
                            
                            // Handle transcription drop
                            if (draggedTranscription && deal.meetings[0]) {
                              e.preventDefault();
                              e.stopPropagation();
                              
                              const meetingId = deal.meetings[0].id;
                              const transcriptionId = draggedTranscription.id;
                              
                              const updatedLinks = {
                                ...meetingTranscriptions,
                                [meetingId]: transcriptionId
                              };
                              setMeetingTranscriptions(updatedLinks);
                              localStorage.setItem('meeting_transcription_links', JSON.stringify(updatedLinks));
                              
                              console.log(`✅ Linked transcription "${draggedTranscription.title}" to meeting "${deal.organization}"`);
                              setDraggedTranscription(null);
                            }
                            // Handle card reordering within same column only
                            else if (draggedMeeting && draggedCardIndex !== null && draggedColumnId === stage.id && draggedCardIndex !== idx) {
                              e.preventDefault();
                              e.stopPropagation();
                              
                              const dealsArray = Object.entries(groupedMeetings);
                              const [movedDeal] = dealsArray.splice(draggedCardIndex, 1);
                              dealsArray.splice(idx, 0, movedDeal);
                              
                              // Save custom order to localStorage
                              const customOrder = JSON.parse(localStorage.getItem('kanban_card_order') || '{}');
                              const stageCards = dealsArray.map(([_, meetings]) => meetings[0].id);
                              customOrder[stage.id] = stageCards;
                              localStorage.setItem('kanban_card_order', JSON.stringify(customOrder));
                              
                              // If Excel data source, also save to backend for persistence
                              if (dataSource === 'excel') {
                                saveExcelCardOrder(customOrder);
                              }
                              
                              // Force re-render by updating meetings
                              setMeetings([...meetings]);
                              setDraggedCardIndex(null);
                              setDraggedColumnId(null);
                            }
                            // For cross-column moves, let the column handler take care of it
                            // Don't call preventDefault/stopPropagation so event bubbles up
                          }}
                          className="bg-black border border-white/40 rounded-lg p-3 cursor-grab active:cursor-grabbing hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300"
                          onClick={() => {
                            setSelectedMeeting(deal.meetings[0]);
                            setSelectedOrgMeetings(deal.meetings);
                          }}
                        >
                          {/* Organization Name (Deal Name) */}
                          <div className="flex items-center justify-between mb-1.5">
                            <h4 className="text-white font-bold text-sm flex-1 truncate">
                              {deal.organization}
                            </h4>
                            <div className="flex items-center gap-1 ml-2">
                              {/* Documents Badge */}
                              {deal.meetings[0]?.documents && deal.meetings[0].documents.length > 0 && (
                                <span className="bg-purple-600/30 text-purple-300 px-1.5 py-0.5 rounded text-[9px] font-semibold flex items-center gap-1">
                                  <FileText size={10} />
                                  {deal.meetings[0].documents.length}
                                </span>
                              )}
                              {/* Transcription Linked Badge */}
                              {meetingTranscriptions[deal.meetings[0]?.id] && (
                                <span className="bg-cyan-600/30 text-cyan-300 px-1.5 py-0.5 rounded text-[9px] font-semibold flex items-center gap-1">
                                  <FileAudio size={10} />
                                  Transcript
                                </span>
                              )}
                              {/* Delete Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm(`Delete meeting with ${deal.organization}?`)) {
                                    handleDeleteMeeting(deal.meetings[0]);
                                  }
                                }}
                                className="p-1 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded transition-colors"
                                title="Delete meeting"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                          
                          {/* Meeting Date */}
                          <div className="flex items-center gap-1.5 text-cyan-400 text-[11px] mb-2 font-semibold">
                            <Calendar size={11} />
                            <span>
                              {(() => {
                                const meetingDate = oldestMeeting.date;
                                if (!meetingDate || meetingDate === 'Invalid Date') {
                                  return 'TBD';
                                }
                                try {
                                  const date = new Date(meetingDate);
                                  if (isNaN(date.getTime())) {
                                    return 'TBD';
                                  }
                                  return date.toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric',
                                    year: 'numeric'
                                  });
                                } catch {
                                  return 'TBD';
                                }
                              })()}
                            </span>
                          </div>
                          
                          {/* Meeting Status Selector */}
                          <div className="flex items-center gap-1.5 mb-2" onClick={(e) => e.stopPropagation()}>
                            <select
                              value={getMeetingDisplayStatus(deal.meetings[0])}
                              onChange={(e) => {
                                e.stopPropagation();
                                const newStatus = e.target.value;
                                updateMeetingStatus(deal.meetings[0].id, newStatus);
                                // Also update pipeline stage to match
                                const updatedMeetings = meetings.map(m => 
                                  m.id === deal.meetings[0].id 
                                    ? { ...m, pipeline_stage: newStatus }
                                    : m
                                );
                                setMeetings(updatedMeetings);
                                // Save to localStorage
                                const stagesMap = updatedMeetings.reduce((acc, m) => {
                                  acc[m.id] = m.pipeline_stage || 'tbd';
                                  return acc;
                                }, {});
                                localStorage.setItem('meeting_pipeline_stages', JSON.stringify(stagesMap));
                              }}
                              className={`text-[10px] font-semibold px-2 py-1 rounded cursor-pointer border-0 outline-none ${
                                getMeetingDisplayStatus(deal.meetings[0]) === 'done' 
                                  ? 'bg-green-600/30 text-green-300'
                                  : getMeetingDisplayStatus(deal.meetings[0]) === 'in_progress'
                                  ? 'bg-orange-600/30 text-orange-300'
                                  : getMeetingDisplayStatus(deal.meetings[0]) === 'upcoming'
                                  ? 'bg-yellow-600/30 text-yellow-300'
                                  : 'bg-blue-600/30 text-blue-300'
                              }`}
                            >
                              <option value="tbd" className="bg-gray-900 text-blue-300">📋 TBD</option>
                              <option value="upcoming" className="bg-gray-900 text-yellow-300">📅 Upcoming</option>
                              <option value="in_progress" className="bg-gray-900 text-orange-300">🔄 In Progress</option>
                              <option value="done" className="bg-gray-900 text-green-300">✅ Done</option>
                            </select>
                          </div>
                          
                          {/* Location */}
                          <div className="flex items-center gap-1.5 text-white/60 text-[10px] mb-2">
                            <MapPin size={10} />
                            <span className="truncate">{deal.location || 'Location TBD'}</span>
                          </div>
                          
                          {/* Contact Info */}
                          {primaryParticipant && (
                            <div className="flex items-center gap-1.5 mb-2">
                              {primaryParticipant.image_url ? (
                                <img 
                                  src={primaryParticipant.image_url} 
                                  alt={primaryParticipant.name}
                                  className="w-6 h-6 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center text-white text-[10px] font-bold">
                                  {primaryParticipant.name?.charAt(0) || 'U'}
                                </div>
                              )}
                              <span className="text-white text-xs truncate">{primaryParticipant.name}</span>
                            </div>
                          )}
                          
                          {/* Agenda Description */}
                          <div className="text-white/80 text-xs mb-2 line-clamp-2">
                            {oldestMeeting.agenda || `Meeting with ${primaryParticipant?.name || 'contact'}${deal.organization ? ` from ${deal.organization}` : ''}`}
                          </div>
                          
                          {/* Meeting Count Badge */}
                          {deal.meetings.length > 1 && (
                            <div className="mb-2">
                              <span className="bg-blue-600/20 text-blue-300 px-2 py-0.5 rounded text-[10px] font-semibold">
                                {deal.meetings.length} meetings
                              </span>
                            </div>
                          )}
                          
                          {/* Individual Meeting Details */}
                          {deal.meetings.length > 1 && (
                            <div className="mb-2 space-y-1 bg-white/5 rounded p-2">
                              {deal.meetings.slice(0, 3).map((meeting, mIdx) => {
                                const mDate = new Date(meeting.date);
                                return (
                                  <div key={mIdx} className="flex items-center justify-between text-[10px] text-white/60">
                                    <span>{mDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                    <span>{meeting.meeting_type === 'virtual' ? '💻 Virtual' : '🏢 In-Person'}</span>
                                  </div>
                                );
                              })}
                              {deal.meetings.length > 3 && (
                                <div className="text-[10px] text-white/40 text-center">
                                  +{deal.meetings.length - 3} more
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Win Percentage */}
                          <div className="space-y-0.5">
                            <div className="flex items-center justify-between text-[10px]">
                              <span className="text-white/60">Win %</span>
                              <span className="text-white font-semibold">{winPercentage}%</span>
                            </div>
                            <div className="w-full bg-white/10 rounded-full h-1 overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${
                                  winPercentage >= 80 ? 'bg-green-500' :
                                  winPercentage >= 60 ? 'bg-yellow-500' :
                                  'bg-orange-500'
                                }`}
                                style={{ width: `${winPercentage}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Empty State */}
                    {dealCards.length === 0 && (
                      <div className="text-center text-gray-500 py-8">
                        <p className="text-xs">No meetings in this stage</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            </div>
            
            {/* Transcription Panel - Hidden on mobile */}
            <div className="hidden lg:block w-80 bg-black border border-white/20 rounded-xl p-4 overflow-y-auto" style={{ maxHeight: '880px' }}>
              {/* Live Recording Section */}
              <div className="mb-4 bg-gradient-to-r from-red-900/40 to-red-800/40 border border-red-700/50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Mic className="text-red-400" size={20} />
                    <div>
                      <h4 className="text-white font-bold text-sm">Live Recording</h4>
                      <p className="text-white/60 text-xs">Record your meeting or conversation</p>
                    </div>
                  </div>
                </div>
                
                {/* Audio Waveform Visualization */}
                {isRecording && (
                  <div className="flex items-center justify-center gap-1 h-12 mb-2">
                    {audioLevels.map((level, idx) => (
                      <div
                        key={idx}
                        className="w-2 bg-red-500 rounded-full transition-all duration-75"
                        style={{ height: `${Math.max(4, level * 0.4)}px` }}
                      />
                    ))}
                  </div>
                )}
                
                {/* Recording Button */}
                <button
                  onClick={() => {
                    if (!isRecording) {
                      startRecording();
                    } else {
                      stopRecording();
                    }
                  }}
                  disabled={transcribing}
                  className={`w-full py-2.5 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                    isRecording
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : transcribing
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white'
                  }`}
                >
                  <Mic size={16} />
                  {isRecording ? `Stop Recording ${Math.floor(recordingTime / 60)}:${(recordingTime % 60).toString().padStart(2, '0')}` : 'Start Recording'}
                </button>
                
                {/* Save & Transcribe Button - Shows after recording stops */}
                {audioBlob && !isRecording && (
                  <button
                    onClick={transcribeRecording}
                    disabled={transcribing}
                    className={`w-full mt-2 py-2.5 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                      transcribing
                        ? 'bg-cyan-800 text-cyan-200 cursor-wait'
                        : 'bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800 text-white'
                    }`}
                  >
                    {transcribing ? (
                      <>
                        <Loader className="animate-spin" size={16} />
                        Transcribing...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={16} />
                        Save & Transcribe ({Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')})
                      </>
                    )}
                  </button>
                )}
                
                {/* Error Message */}
                {transcriptionError && (
                  <div className="mt-2 p-2 bg-red-500/20 border border-red-500/50 rounded-lg">
                    <p className="text-red-300 text-xs">{transcriptionError}</p>
                  </div>
                )}
              </div>

              {/* Upload Audio File Section - Collapsible */}
              <div className="mb-4 bg-black border border-white/40 rounded-xl overflow-hidden">
                {/* Header - Always Visible */}
                <div 
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/5 transition-colors"
                  onClick={() => setIsUploadExpanded(!isUploadExpanded)}
                >
                  <div className="flex items-center gap-2">
                    <Upload className="text-cyan-500" size={18} />
                    <h4 className="text-white font-bold text-sm">Upload Audio File</h4>
                  </div>
                  <ChevronDown 
                    className={`text-white/60 transition-transform duration-200 ${isUploadExpanded ? 'rotate-180' : ''}`}
                    size={20}
                  />
                </div>
                
                {/* Collapsible Content */}
                {isUploadExpanded && (
                  <div className="px-4 pb-4">
                    <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-white/30 rounded-lg cursor-pointer hover:bg-white/5 transition-colors mb-3">
                      <div className="flex flex-col items-center justify-center">
                        <FileAudio className="text-cyan-500 mb-1" size={24} />
                        <p className="text-xs text-gray-300">
                          <span className="font-semibold">Click to upload</span> or drag and drop
                        </p>
                        <p className="text-[10px] text-gray-500 mt-1">
                          MP3, WAV, M4A, WEBM (Max 25MB)
                        </p>
                        {selectedFile && (
                          <p className="mt-1 text-xs text-cyan-400 font-medium">
                            ✓ {selectedFile.name}
                          </p>
                        )}
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept=".mp3,.mp4,.mpeg,.mpga,.m4a,.wav,.webm"
                        onChange={handleFileSelect}
                      />
                    </label>

                    <div className="mb-3">
                      <label className="block text-xs font-medium text-gray-300 mb-1">
                        Language (Optional)
                      </label>
                      <input
                        type="text"
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        placeholder="e.g., en, es, fr"
                        className="w-full bg-white/10 border border-white/40 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                      />
                      <p className="text-[10px] text-gray-500 mt-1">ISO 639-1 code</p>
                    </div>

                    {transcriptionError && (
                      <div className="mb-3 p-2 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-2 text-red-300 text-xs">
                        <AlertCircle size={14} />
                        <span>{transcriptionError}</span>
                      </div>
                    )}

                    <button
                      onClick={handleTranscribe}
                      disabled={!selectedFile || transcribing}
                      className={`w-full py-2.5 font-semibold text-sm ${
                        !selectedFile || transcribing
                          ? 'bg-gray-600 cursor-not-allowed opacity-50'
                          : 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700'
                      } text-white rounded-lg transition-all flex items-center justify-center gap-2`}
                    >
                      {transcribing ? (
                        <>
                          <Loader className="animate-spin" size={16} />
                          Transcribing...
                        </>
                      ) : (
                        <>
                          <Mic size={16} />
                          Transcribe Audio
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              {/* Saved Conversations Header */}
              <div className="flex items-center justify-between mb-3 pt-3 border-t border-white/10">
                <h3 className="text-white font-bold text-sm flex items-center gap-2">
                  <FileAudio size={18} />
                  Saved Conversations
                </h3>
                <span className="text-white/60 text-xs">({conversations.length})</span>
              </div>
              
              {/* Category Filter */}
              <div className="mb-4">
                <select
                  value={selectedTranscriptCategory}
                  onChange={(e) => setSelectedTranscriptCategory(e.target.value)}
                  className="w-full bg-white/10 border border-white/40 rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-blue-500"
                >
                  {transcriptCategories.map(cat => (
                    <option key={cat} value={cat.toLowerCase()}>{cat}</option>
                  ))}
                </select>
              </div>
              
              {/* Transcriptions List */}
              <div className="space-y-2">
                {conversations
                  .filter(c => selectedTranscriptCategory === 'all' || (c.category || 'Other').toLowerCase() === selectedTranscriptCategory.toLowerCase())
                  .map((conversation) => {
                    const linkedMeetings = Object.entries(meetingTranscriptions)
                      .filter(([_, transcriptId]) => transcriptId === conversation.id)
                      .map(([meetingId]) => meetings.find(m => m.id === meetingId))
                      .filter(Boolean);
                    
                    return (
                      <div
                        key={conversation.id}
                        draggable
                        onDragStart={(e) => {
                          setDraggedTranscription(conversation);
                          e.dataTransfer.effectAllowed = 'link';
                        }}
                        onDragEnd={() => setDraggedTranscription(null)}
                        className="bg-white/5 border border-white/20 rounded-lg p-3 hover:bg-white/10 hover:border-blue-500/50 transition-all cursor-grab active:cursor-grabbing relative group"
                        onClick={() => {
                          setExpandedConversation(conversation);
                        }}
                      >
                        {/* Delete Button - Shows on hover */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete "${conversation.title}"?`)) {
                              handleDeleteConversation(conversation.id);
                            }
                          }}
                          className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100 transition-all"
                          title="Delete conversation"
                        >
                          <Trash2 size={14} />
                        </button>
                        
                        <div className="flex items-start justify-between mb-2 pr-6">
                          <h4 className="text-white font-semibold text-sm line-clamp-2 flex-1">
                            {conversation.title}
                          </h4>
                          {linkedMeetings.length > 0 && (
                            <span className="ml-2 bg-blue-600/30 text-blue-300 px-1.5 py-0.5 rounded text-[10px] font-semibold">
                              Linked
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2 text-white/60 text-xs mb-2">
                          <Calendar size={12} />
                          <span>
                            {(() => {
                              const dateStr = conversation.created_at || conversation.date;
                              if (!dateStr) return 'No Date';
                              const date = new Date(dateStr);
                              if (isNaN(date.getTime())) return 'No Date';
                              return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                            })()}
                          </span>
                        </div>
                        
                        {conversation.category && (
                          <div className="mb-2">
                            <span className="inline-block bg-purple-600/20 text-purple-300 px-2 py-0.5 rounded text-[10px]">
                              {conversation.category}
                            </span>
                          </div>
                        )}
                        
                        {linkedMeetings.length > 0 && (
                          <div className="text-[10px] text-white/50">
                            Linked to: {linkedMeetings.map(m => m.organization || m.title).join(', ')}
                          </div>
                        )}
                        
                        {conversation.transcript && (
                          <div className="mt-2 text-white/50 text-[10px] line-clamp-2">
                            {conversation.transcript.substring(0, 80)}...
                          </div>
                        )}
                      </div>
                    );
                  })}
                
                {conversations.filter(c => selectedTranscriptCategory === 'all' || (c.category || 'Other').toLowerCase() === selectedTranscriptCategory.toLowerCase()).length === 0 && (
                  <div className="text-center py-8 text-white/40 text-sm">
                    No transcriptions found
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Meetings Table */}
        {viewMode === 'list' && (
        <div className="bg-black border border-gray-400 rounded-xl overflow-hidden hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
          <div className="overflow-x-auto">
            <div className="inline-block min-w-full">
              <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                <table className="w-full">
                  <thead className="bg-black border-b border-white/60 sticky top-0 z-10">
                    <tr>
                      <th 
                        className="text-left px-6 py-4 text-sm font-semibold text-white cursor-pointer hover:text-white bg-transparent"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center gap-2">
                          Participant
                          {sortField === 'name' && (
                            sortOrder === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                          )}
                        </div>
                      </th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-white bg-transparent">Agenda</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-white bg-transparent">Organization</th>
                      <th 
                        className="text-left px-6 py-4 text-sm font-semibold text-white cursor-pointer hover:text-white whitespace-nowrap bg-transparent"
                        onClick={() => handleSort('date')}
                      >
                        <div className="flex items-center gap-2">
                          Date & Time
                          {sortField === 'date' && (
                            sortOrder === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                          )}
                        </div>
                      </th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-white bg-transparent">Type</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-white bg-transparent">Transcription</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-white bg-transparent">Status</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-white bg-transparent">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                {(() => {
                  // Sort list view meetings: Upcoming → TBD → Completed
                  const sortedListMeetings = [...filteredMeetings].sort((a, b) => {
                    const isValidDate = (dateStr) => {
                      if (!dateStr) return false;
                      const date = new Date(dateStr);
                      return !isNaN(date.getTime());
                    };
                    
                    const aHasDate = isValidDate(a.date);
                    const bHasDate = isValidDate(b.date);
                    
                    if (!aHasDate && !bHasDate) return 0;
                    if (!aHasDate) return a.status === 'done' ? 1 : 1;
                    if (!bHasDate) return b.status === 'done' ? -1 : -1;
                    
                    const aStatus = a.status || 'upcoming';
                    const bStatus = b.status || 'upcoming';
                    
                    if (aStatus === 'done' && bStatus !== 'done') return 1;
                    if (bStatus === 'done' && aStatus !== 'done') return -1;
                    
                    const aDate = new Date(a.date);
                    const bDate = new Date(b.date);
                    return aDate - bDate;
                  });
                  
                  if (sortedListMeetings.length === 0) {
                    return (
                      <tr>
                        <td colSpan="8" className="px-6 py-12 text-center text-white">
                          No meetings found. Click &quot;New Meeting&quot; to create one.
                        </td>
                      </tr>
                    );
                  }
                  
                  return sortedListMeetings.map((meeting) => {
                    const status = getMeetingDisplayStatus(meeting);
                    const primaryParticipant = meeting.participants && meeting.participants.length > 0 
                      ? meeting.participants[0] 
                      : null;
                    const meetingLinkType = getMeetingLinkType(meeting.meeting_link);

                    return (
                      <tr 
                        key={meeting.id} 
                        className="hover:bg-white/5 transition-colors cursor-pointer"
                        onClick={() => setSelectedMeeting(meeting)}
                      >
                        {/* Participant - Name Only */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            {primaryParticipant?.image_url ? (
                              <img 
                                src={primaryParticipant.image_url} 
                                alt={primaryParticipant.name}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                                <User size={20} className="text-black" />
                              </div>
                            )}
                            <div className="text-white font-medium">
                              {primaryParticipant?.name || 'No participant'}
                            </div>
                          </div>
                        </td>

                        {/* Agenda */}
                        <td className="px-6 py-4">
                          <div className="text-white text-sm">
                            {meeting.agenda ? (
                              <div className="line-clamp-2">
                                {meeting.agenda}
                              </div>
                            ) : (
                              <span className="text-white">-</span>
                            )}
                          </div>
                        </td>

                        {/* Organization */}
                        <td className="px-6 py-4">
                          <div className="text-white">
                            {primaryParticipant?.company || '-'}
                          </div>
                          {primaryParticipant?.role && (
                            <div className="text-xs text-white">{primaryParticipant.role}</div>
                          )}
                        </td>

                        {/* Date & Time (CST) */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-white">{formatDate(meeting.date)}</div>
                          <div className="text-sm text-white">
                            {formatTimeCST(meeting.time)} ({meeting.duration || 60} min) CST
                          </div>
                        </td>

                        {/* Type */}
                        <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                          {meeting.meeting_type === 'virtual' && meeting.meeting_link ? (
                            <a
                              href={meeting.meeting_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-white hover:text-gray-200 transition-colors"
                            >
                              <Video size={16} />
                              <span>{meetingLinkType} Link</span>
                              <ExternalLink size={14} />
                            </a>
                          ) : meeting.meeting_type === 'virtual' ? (
                            <div className="flex items-center gap-2">
                              <Video size={16} className="text-white" />
                              <span className="text-white">Virtual</span>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center gap-2">
                                <MapPin size={16} className="text-green-400" />
                                <span className="text-white">In-Person</span>
                              </div>
                              {meeting.location && (
                                <div className="text-xs text-white mt-1">{meeting.location}</div>
                              )}
                            </>
                          )}
                        </td>

                        {/* Transcription */}
                        <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                          {meeting.transcript ? (
                            <button
                              onClick={() => setViewingTranscription(meeting)}
                              className="flex items-center gap-2 text-green-400 hover:text-green-300 transition-colors"
                            >
                              <FileText size={16} />
                              <span>Yes</span>
                            </button>
                          ) : (
                            <span className="text-white">No</span>
                          )}
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${
                            status === 'upcoming' ? 'bg-transparent border-white/60 text-white' :
                            status === 'in_progress' ? 'bg-green-600/30 text-green-300 animate-pulse border-green-500/30' :
                            'bg-transparent border-white/60 text-white'
                          }`}>
                            {status === 'upcoming' ? 'Upcoming' : status === 'in_progress' ? 'In Progress' : 'Completed'}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEdit(meeting)}
                              className="text-white hover:text-gray-200 transition-colors"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(meeting.id)}
                              className="text-red-400 hover:text-red-300 transition-colors"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  });
                })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Create/Edit Modal */}
        {(isCreating || isEditing) && (
          <div className="fixed inset-0 bg-transparent backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-black rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white">
                    {isEditing ? 'Edit Meeting' : 'New Meeting'}
                  </h2>
                  <button onClick={resetForm} className="text-white hover:text-white">
                    <X size={24} />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Title */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-white mb-2">
                        Meeting Title *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="w-full px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white focus:outline-none focus:border-blue-500"
                        placeholder="e.g., Q4 Strategy Review"
                      />
                    </div>

                    {/* Date */}
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Date *
                      </label>
                      <input
                        type="date"
                        required
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        className="w-full px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white focus:outline-none focus:border-blue-500"
                      />
                    </div>

                    {/* Time */}
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Time *
                      </label>
                      <input
                        type="time"
                        required
                        value={formData.time}
                        onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                        className="w-full px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white focus:outline-none focus:border-blue-500"
                      />
                    </div>

                    {/* Duration */}
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Duration (minutes)
                      </label>
                      <input
                        type="number"
                        value={formData.duration}
                        onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                        className="w-full px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white focus:outline-none focus:border-blue-500"
                        placeholder="60"
                      />
                    </div>

                    {/* Meeting Type */}
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Meeting Type *
                      </label>
                      <select
                        value={formData.meeting_type}
                        onChange={(e) => setFormData({ ...formData, meeting_type: e.target.value })}
                        className="w-full px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white focus:outline-none focus:border-blue-500"
                      >
                        <option value="virtual">Virtual</option>
                        <option value="in-person">In-Person</option>
                      </select>
                    </div>

                    {/* Category */}
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Category *
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white focus:outline-none focus:border-blue-500"
                      >
                        {categories.filter(c => c !== 'All').map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>

                    {/* Meeting Link (for virtual) */}
                    {formData.meeting_type === 'virtual' && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-white mb-2">
                          Meeting Link
                        </label>
                        <input
                          type="url"
                          value={formData.meeting_link}
                          onChange={(e) => setFormData({ ...formData, meeting_link: e.target.value })}
                          className="w-full px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white focus:outline-none focus:border-blue-500"
                          placeholder="https://zoom.us/j/..."
                        />
                      </div>
                    )}

                    {/* Location (for in-person) */}
                    {formData.meeting_type === 'in-person' && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-white mb-2">
                          Location
                        </label>
                        <input
                          type="text"
                          value={formData.location}
                          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                          className="w-full px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white focus:outline-none focus:border-blue-500"
                          placeholder="Office address or location"
                        />
                      </div>
                    )}
                  </div>

                  {/* Participants */}
                  <div className="border-t border-white/10 pt-4">
                    <label className="block text-sm font-medium text-white mb-3">
                      Participants
                    </label>
                    
                    {/* Add Participant Form */}
                    <div className="bg-white/5 rounded-lg p-4 mb-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                        <input
                          type="text"
                          placeholder="Name *"
                          value={newParticipant.name}
                          onChange={(e) => setNewParticipant({ ...newParticipant, name: e.target.value })}
                          className="px-3 py-2 bg-white/5 border border-white/60 rounded text-white text-sm focus:outline-none focus:border-blue-500"
                        />
                        <input
                          type="email"
                          placeholder="Email"
                          value={newParticipant.email}
                          onChange={(e) => setNewParticipant({ ...newParticipant, email: e.target.value })}
                          className="px-3 py-2 bg-white/5 border border-white/60 rounded text-white text-sm focus:outline-none focus:border-blue-500"
                        />
                        <input
                          type="text"
                          placeholder="Company/Organization"
                          value={newParticipant.company}
                          onChange={(e) => setNewParticipant({ ...newParticipant, company: e.target.value })}
                          className="px-3 py-2 bg-white/5 border border-white/60 rounded text-white text-sm focus:outline-none focus:border-blue-500"
                        />
                        <input
                          type="text"
                          placeholder="Role/Title"
                          value={newParticipant.role}
                          onChange={(e) => setNewParticipant({ ...newParticipant, role: e.target.value })}
                          className="px-3 py-2 bg-white/5 border border-white/60 rounded text-white text-sm focus:outline-none focus:border-blue-500"
                        />
                        <input
                          type="url"
                          placeholder="Image URL (optional)"
                          value={newParticipant.image_url}
                          onChange={(e) => setNewParticipant({ ...newParticipant, image_url: e.target.value })}
                          disabled={newParticipant.image_file !== null}
                          className="px-3 py-2 bg-white/5 border border-white/60 rounded text-white text-sm focus:outline-none focus:border-blue-500 disabled:opacity-50"
                        />
                        <div>
                          <label className="block text-xs text-white mb-1">Or upload image</label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setNewParticipant({ 
                              ...newParticipant, 
                              image_file: e.target.files[0],
                              image_url: '' 
                            })}
                            className="text-sm text-white file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:bg-gradient-to-r bg-white file:text-white hover:file:bg-black cursor-pointer"
                          />
                          {newParticipant.image_file && (
                            <div className="text-xs text-green-400 mt-1">
                              Selected: {newParticipant.image_file.name}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={addParticipant}
                        disabled={uploadingImage}
                        className="text-sm bg-gradient-to-r bg-white hover:bg-gray-100 text-black px-4 py-2 rounded transition-colors disabled:opacity-50"
                      >
                        {uploadingImage ? 'Uploading...' : 'Add Participant'}
                      </button>
                    </div>

                    {/* Participants List */}
                    {formData.participants.length > 0 && (
                      <div className="space-y-2">
                        {formData.participants.map((participant, index) => (
                          <div key={index} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                            <div className="flex items-center gap-3">
                              {participant.image_url ? (
                                <img src={participant.image_url} alt={participant.name} className="w-8 h-8 rounded-full object-cover" />
                              ) : (
                                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                                  <User size={16} className="text-black" />
                                </div>
                              )}
                              <div>
                                <div className="text-white text-sm font-medium">{participant.name}</div>
                                {participant.company && (
                                  <div className="text-xs text-white">{participant.company}</div>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeParticipant(index)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Agenda */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Agenda
                    </label>
                    <textarea
                      value={formData.agenda}
                      onChange={(e) => setFormData({ ...formData, agenda: e.target.value })}
                      rows="3"
                      className="w-full px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white focus:outline-none focus:border-blue-500"
                      placeholder="Meeting agenda..."
                    />
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Notes
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows="3"
                      className="w-full px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white focus:outline-none focus:border-blue-500"
                      placeholder="Additional notes..."
                    />
                  </div>

                  {/* Transcript */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Transcript
                    </label>
                    <textarea
                      value={formData.transcript}
                      onChange={(e) => setFormData({ ...formData, transcript: e.target.value })}
                      rows="6"
                      className="w-full px-4 py-2 bg-white/5 border border-white/60 rounded-lg text-white focus:outline-none focus:border-blue-500 font-mono text-sm"
                      placeholder="Meeting transcript will appear here..."
                    />
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      className="flex-1 bg-gradient-to-r bg-white hover:bg-gray-100 text-black py-3 rounded-lg font-medium transition-colors"
                    >
                      {isEditing ? 'Update Meeting' : 'Create Meeting'}
                    </button>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-6 py-3 bg-transparent hover:bg-white/5 text-white rounded-lg font-medium transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Meeting Details Modal */}
        {selectedMeeting && (
          <div className="fixed inset-0 bg-transparent backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-black border border-white/40 rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white">
                    {selectedOrgMeetings.length > 1 ? `${selectedMeeting.organization || 'Organization'} - ${selectedOrgMeetings.length} Meetings` : 'Meeting Details'}
                  </h2>
                  <button onClick={() => { setSelectedMeeting(null); setSelectedOrgMeetings([]); }} className="text-white hover:text-gray-300">
                    <X size={24} />
                  </button>
                </div>

                {/* Show all meetings if multiple */}
                {selectedOrgMeetings.length > 1 ? (
                  <div className="space-y-4">
                    {selectedOrgMeetings.map((meeting, idx) => (
                      <div key={idx} className="bg-white/5 border border-white/20 rounded-lg p-5 hover:border-white/40 transition-all">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-white text-lg font-bold mb-1">Meeting #{idx + 1}</h3>
                            <p className="text-white/60 text-sm">{meeting.title}</p>
                          </div>
                          <span className={`px-3 py-1 rounded text-xs font-semibold ${
                            meeting.status === 'done' ? 'bg-white/10 text-white' :
                            meeting.status === 'in_progress' ? 'bg-green-600/30 text-green-300' :
                            'bg-blue-600/30 text-blue-300'
                          }`}>
                            {meeting.status === 'done' ? 'Completed' : 
                             meeting.status === 'in_progress' ? 'In Progress' : 'Upcoming'}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-4">
                          {/* Date & Time */}
                          <div>
                            <label className="block text-xs font-medium text-white/60 mb-1">Date & Time</label>
                            <div className="text-white text-sm">
                              {formatDate(meeting.date)} at {formatTimeTo12Hour(meeting.time)}
                            </div>
                          </div>

                          {/* Duration */}
                          <div>
                            <label className="block text-xs font-medium text-white/60 mb-1">Duration</label>
                            <div className="text-white text-sm">{meeting.duration || 120} minutes</div>
                          </div>

                          {/* Meeting Type */}
                          <div>
                            <label className="block text-xs font-medium text-white/60 mb-1">Type</label>
                            <div className="flex items-center gap-2 text-white text-sm">
                              {meeting.meeting_type === 'virtual' ? (
                                <>
                                  <Video size={14} />
                                  Virtual
                                </>
                              ) : (
                                <>
                                  <MapPin size={14} />
                                  In-Person
                                </>
                              )}
                            </div>
                          </div>

                          {/* Location/Link */}
                          <div>
                            <label className="block text-xs font-medium text-white/60 mb-1">Location</label>
                            <div className="text-white text-sm">{meeting.location || 'TBD'}</div>
                          </div>
                        </div>

                        {/* Participants */}
                        {meeting.participants && meeting.participants.length > 0 && (
                          <div className="mb-4">
                            <label className="block text-xs font-medium text-white/60 mb-2">Participants</label>
                            <div className="flex items-center gap-2 flex-wrap">
                              {meeting.participants.map((participant, pIdx) => (
                                <div key={pIdx} className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2">
                                  {participant.image_url ? (
                                    <img 
                                      src={participant.image_url} 
                                      alt={participant.name}
                                      className="w-6 h-6 rounded-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                                      {participant.name?.charAt(0) || 'U'}
                                    </div>
                                  )}
                                  <div>
                                    <div className="text-white text-sm font-medium">{participant.name}</div>
                                    {participant.company && (
                                      <div className="text-white/60 text-xs">{participant.company}</div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Agenda */}
                        {meeting.agenda && (
                          <div>
                            <label className="block text-xs font-medium text-white/60 mb-1">Agenda</label>
                            <div className="text-white/80 text-sm">{meeting.agenda}</div>
                          </div>
                        )}

                        {/* Meeting Link */}
                        {meeting.meeting_link && (
                          <div className="mt-3">
                            <a
                              href={meeting.meeting_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300 text-sm inline-flex items-center gap-2"
                            >
                              Join Meeting
                              <ExternalLink size={14} />
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  // Original single meeting view
                <div className="space-y-6">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Title</label>
                    <div className="text-white text-lg">{selectedMeeting.title}</div>
                  </div>

                  {/* Date & Time */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">Date</label>
                      <div className="text-white">{formatDate(selectedMeeting.date)}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">Time</label>
                      <div className="text-white">{formatTimeTo12Hour(selectedMeeting.time)} ({selectedMeeting.duration || 60} min)</div>
                    </div>
                  </div>

                  {/* Type & Link/Location */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Meeting Type</label>
                    <div className="flex items-center gap-2 text-white">
                      {(selectedMeeting.meeting_type || (selectedMeeting.meeting_link ? 'virtual' : 'in-person')) === 'virtual' ? (
                        <>
                          <Video size={16} className="text-white" />
                          Virtual
                        </>
                      ) : (
                        <>
                          <MapPin size={16} className="text-green-400" />
                          In-Person
                        </>
                      )}
                    </div>
                    {selectedMeeting.meeting_link && (
                      <a
                        href={selectedMeeting.meeting_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white hover:text-gray-200 text-sm mt-2 inline-flex items-center gap-2"
                      >
                        Join {getMeetingLinkType(selectedMeeting.meeting_link)} Meeting
                        <ExternalLink size={14} />
                      </a>
                    )}
                    {selectedMeeting.location && (
                      <div className="text-white text-sm mt-2">{selectedMeeting.location}</div>
                    )}
                  </div>

                  {/* Participants */}
                  {selectedMeeting.participants && selectedMeeting.participants.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-white mb-3">Participants</label>
                      <div className="space-y-3">
                        {selectedMeeting.participants.map((participant, index) => (
                          <div key={index} className="flex items-center gap-3 bg-white/5 rounded-lg p-3">
                            {participant.image_url ? (
                              <img src={participant.image_url} alt={participant.name} className="w-10 h-10 rounded-full object-cover" />
                            ) : (
                              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                                <User size={20} className="text-black" />
                              </div>
                            )}
                            <div className="flex-1">
                              <div className="text-white font-medium">{participant.name}</div>
                              {participant.company && <div className="text-sm text-white">{participant.company}</div>}
                              {participant.role && <div className="text-xs text-white">{participant.role}</div>}
                            </div>
                            {participant.email && (
                              <div className="text-sm text-white">{participant.email}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Agenda */}
                  {selectedMeeting.agenda && (
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">Agenda</label>
                      <div className="text-white whitespace-pre-wrap bg-white/5 rounded-lg p-3">
                        {selectedMeeting.agenda}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {selectedMeeting.notes && (
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">Notes</label>
                      <div className="text-white whitespace-pre-wrap bg-white/5 rounded-lg p-3">
                        {selectedMeeting.notes}
                      </div>
                    </div>
                  )}

                  {/* Transcript */}
                  {selectedMeeting.transcript && (
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">Transcript</label>
                      <div className="text-white whitespace-pre-wrap bg-white/5 rounded-lg p-3 font-mono text-sm max-h-64 overflow-y-auto">
                        {selectedMeeting.transcript}
                      </div>
                    </div>
                  )}

                  {/* Documents Section */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Documents</label>
                    
                    {/* Upload Button */}
                    <div className="mb-3">
                      <label className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg cursor-pointer transition-colors border border-white/20">
                        {uploadingDocument && documentUploadMeetingId === selectedMeeting.id ? (
                          <>
                            <Loader size={16} className="animate-spin text-white" />
                            <span className="text-white text-sm">Uploading...</span>
                          </>
                        ) : (
                          <>
                            <Upload size={16} className="text-white" />
                            <span className="text-white text-sm">Upload Document</span>
                          </>
                        )}
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handleDocumentUpload(file, selectedMeeting.id);
                            }
                            e.target.value = ''; // Reset input
                          }}
                          disabled={uploadingDocument}
                        />
                      </label>
                      <span className="ml-3 text-xs text-white/50">
                        PDF, DOC, XLS, PPT, TXT, Images (max 50MB)
                      </span>
                    </div>
                    
                    {/* Documents List */}
                    {selectedMeeting.documents && selectedMeeting.documents.length > 0 ? (
                      <div className="space-y-2">
                        {selectedMeeting.documents.map((doc, idx) => (
                          <div 
                            key={doc.id || idx} 
                            className="flex items-center justify-between bg-white/5 rounded-lg p-3 border border-white/10 hover:border-white/30 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">{getDocumentIcon(doc.content_type, doc.original_filename)}</span>
                              <div>
                                <div className="text-white text-sm font-medium truncate max-w-[300px]">
                                  {doc.original_filename}
                                </div>
                                <div className="text-white/50 text-xs">
                                  {doc.size || 'Unknown size'}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <a
                                href={`${apiUrl}${doc.url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                title="Download"
                              >
                                <Download size={16} className="text-white" />
                              </a>
                              <button
                                onClick={() => handleRemoveDocument(selectedMeeting.id, doc.id)}
                                className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                                title="Remove"
                              >
                                <Trash2 size={16} className="text-red-400" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-white/50 text-sm bg-white/5 rounded-lg p-4 text-center border border-dashed border-white/20">
                        No documents attached. Click &quot;Upload Document&quot; to add files.
                      </div>
                    )}
                  </div>

                  {/* Transcription Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Meeting Transcription</label>
                    <div className="flex gap-2">
                      <select
                        value={meetingTranscriptions[selectedMeeting.id] || ''}
                        onChange={(e) => {
                          const transcriptionId = e.target.value || null;
                          const updatedLinks = {
                            ...meetingTranscriptions,
                            [selectedMeeting.id]: transcriptionId
                          };
                          
                          // Update state
                          setMeetingTranscriptions(updatedLinks);
                          
                          // Save to localStorage for persistence
                          localStorage.setItem('meeting_transcription_links', JSON.stringify(updatedLinks));
                          
                          // Update viewing transcription
                          if (transcriptionId) {
                            const transcription = conversations.find(c => c.id === transcriptionId);
                            if (transcription) {
                              setViewingTranscription(transcription);
                            }
                          } else {
                            setViewingTranscription(null);
                          }
                        }}
                        className="flex-1 bg-black border border-white/40 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-blue-500 hover:border-white/60 transition-colors"
                      >
                        <option value="">Select a transcription...</option>
                        {conversations.map((conversation) => (
                          <option key={conversation.id} value={conversation.id}>
                            {conversation.title} - {new Date(conversation.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </option>
                        ))}
                      </select>
                      
                      {/* View Button */}
                      <button
                        onClick={() => {
                          const transcriptionId = meetingTranscriptions[selectedMeeting.id];
                          if (transcriptionId) {
                            const transcription = conversations.find(c => c.id === transcriptionId);
                            if (transcription) {
                              setViewingTranscription(transcription);
                              setShowTranscriptionPopup(true);
                            }
                          }
                        }}
                        disabled={!meetingTranscriptions[selectedMeeting.id]}
                        className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
                      >
                        View
                      </button>
                    </div>
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Status</label>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${
                      getMeetingStatus(selectedMeeting) === 'upcoming' ? 'bg-transparent border-white/60 text-white' :
                      getMeetingStatus(selectedMeeting) === 'in_progress' ? 'bg-green-600/30 text-green-300 border-green-500/30' :
                      'bg-transparent border-white/60 text-white'
                    }`}>
                      {getMeetingStatus(selectedMeeting) === 'upcoming' ? 'Upcoming' : 
                       getMeetingStatus(selectedMeeting) === 'in_progress' ? 'In Progress' : 'Completed'}
                    </span>
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-6 pt-6 border-t border-white/10 flex gap-3">
                    <button
                      onClick={() => {
                        setSelectedMeeting(null);
                        setSelectedOrgMeetings([]);
                        handleEdit(selectedMeeting);
                      }}
                      className="flex-1 bg-gradient-to-r bg-white hover:bg-gray-100 text-black py-3 rounded-lg font-medium transition-colors"
                    >
                      Edit Meeting
                    </button>
                    <button
                      onClick={() => { setSelectedMeeting(null); setSelectedOrgMeetings([]); }}
                      className="px-6 py-3 bg-transparent hover:bg-white/5 text-white rounded-lg font-medium transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Transcription Popup Modal */}
        {showTranscriptionPopup && viewingTranscription && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <div className="bg-black border border-white/40 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              
              {/* Header */}
              <div className="flex items-start justify-between p-6 border-b border-white/20">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">{viewingTranscription.title}</h2>
                  <div className="flex items-center gap-3 text-sm text-white/60">
                    <span className="bg-purple-600/30 text-purple-300 px-3 py-1 rounded-full text-xs font-semibold">
                      Business
                    </span>
                    <span>
                      {new Date(viewingTranscription.date).toLocaleDateString('en-US', { 
                        month: 'numeric',
                        day: 'numeric',
                        year: 'numeric'
                      })} at {new Date(viewingTranscription.date).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <Clock size={14} />
                      {viewingTranscription.duration}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setShowTranscriptionPopup(false)}
                  className="text-white hover:text-gray-300"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                
                {/* AI Summary Section */}
                <div className="bg-gradient-to-br from-blue-900/30 to-purple-900/20 border border-blue-500/30 rounded-lg p-5">
                  <h3 className="text-xl font-bold text-blue-300 mb-4">AI Summary</h3>
                  
                  {/* Summary Text */}
                  {viewingTranscription.summary && (
                    <div className="mb-4">
                      <p className="text-white/90 text-sm leading-relaxed">
                        {viewingTranscription.summary}
                      </p>
                    </div>
                  )}

                  {/* Features Demonstrated */}
                  {viewingTranscription.keyPoints && viewingTranscription.keyPoints.length > 0 && (
                    <div className="mb-4">
                      <p className="text-white font-semibold text-sm mb-2">Features Demonstrated:</p>
                      <ul className="space-y-1">
                        {viewingTranscription.keyPoints.map((point, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-white/80 text-sm">
                            <span className="text-blue-400 mt-0.5">•</span>
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Action Items as Compliance */}
                  {viewingTranscription.actionItems && viewingTranscription.actionItems.length > 0 && (
                    <div className="mb-4">
                      <p className="text-white font-semibold text-sm mb-2">Compliance & Security:</p>
                      <ul className="space-y-1">
                        {viewingTranscription.actionItems.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-white/80 text-sm">
                            <span className="text-blue-400 mt-0.5">•</span>
                            <span>{item.task}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Participants as Client Feedback */}
                  {viewingTranscription.participants && viewingTranscription.participants.length > 0 && (
                    <div className="mb-4">
                      <p className="text-white font-semibold text-sm mb-2">Participants:</p>
                      <p className="text-white/80 text-sm">
                        {viewingTranscription.participants.join(', ')}
                      </p>
                    </div>
                  )}

                  {/* Additional Info */}
                  <div className="space-y-1 text-sm">
                    <p className="text-white/80">
                      <span className="font-semibold text-white">Industry:</span> Real Estate
                    </p>
                    <p className="text-white/80">
                      <span className="font-semibold text-white">Demo Duration:</span> {viewingTranscription.duration}
                    </p>
                  </div>
                </div>

                {/* Full Conversation Section */}
                <div>
                  <h3 className="text-xl font-bold text-blue-300 mb-4">Full Conversation</h3>
                  
                  {viewingTranscription.transcription && (
                    <div className="space-y-4">
                      {/* Parse and display conversation with speakers */}
                      {viewingTranscription.transcription.split('\n').map((line, idx) => {
                        // Check if line starts with a speaker name (format: "Name:")
                        const speakerMatch = line.match(/^([A-Z][a-z]+):\s*(.+)/);
                        
                        if (speakerMatch) {
                          return (
                            <div key={idx} className="flex gap-3">
                              <span className="text-cyan-400 font-semibold text-sm min-w-[80px]">
                                {speakerMatch[1]}:
                              </span>
                              <p className="text-white/90 text-sm leading-relaxed flex-1">
                                {speakerMatch[2]}
                              </p>
                            </div>
                          );
                        } else if (line.trim()) {
                          // Regular text line (continuation of previous speaker)
                          return (
                            <p key={idx} className="text-white/90 text-sm leading-relaxed pl-[92px]">
                              {line}
                            </p>
                          );
                        }
                        return null;
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer with Close Button */}
              <div className="p-6 border-t border-white/20">
                <button
                  onClick={() => setShowTranscriptionPopup(false)}
                  className="w-full bg-transparent hover:bg-white/5 border border-white/40 hover:border-white/60 text-white py-3 rounded-lg font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Expanded Conversation Modal */}
        {expandedConversation && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-black border-2 border-white/30 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white">{expandedConversation.title}</h2>
                  <button onClick={() => setExpandedConversation(null)} className="text-white hover:text-gray-300">
                    <X size={24} />
                  </button>
                </div>

                {/* Category Badge */}
                <div className="mb-4">
                  <span className="px-3 py-1.5 bg-purple-600/30 text-purple-300 rounded-full text-sm">
                    {expandedConversation.category || 'Other'}
                  </span>
                </div>

                {/* Metadata */}
                <div className="flex items-center gap-4 text-sm text-gray-400 mb-6">
                  <span className="flex items-center gap-1">
                    <Calendar size={16} />
                    {new Date(expandedConversation.created_at).toLocaleDateString()} at {new Date(expandedConversation.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock size={16} />
                    {formatTime(Math.floor(expandedConversation.duration))}
                  </span>
                </div>

                {/* AI Summary */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-cyan-400 mb-3">AI Summary</h3>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                    <pre className="whitespace-pre-wrap font-sans text-gray-300 text-sm">
                      {expandedConversation.ai_summary || expandedConversation.summary}
                    </pre>
                  </div>
                </div>

                {/* Full Transcript as Chat */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-purple-400 mb-3">Full Conversation</h3>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-4 space-y-3 max-h-96 overflow-y-auto">
                    {parseTranscriptToMessages(expandedConversation.transcript).map((msg, idx) => (
                      <div key={idx} className="text-sm">
                        <div className="font-semibold text-cyan-400 mb-1">{msg.speaker}:</div>
                        <div className="text-gray-300 pl-4">{msg.text}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-white/10">
                  <button
                    onClick={() => setExpandedConversation(null)}
                    className="w-full bg-transparent hover:bg-white/5 text-white py-3 rounded-lg font-medium transition-colors border border-white/20"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Transcription Modal */}
        {viewingTranscription && (
          <div className="fixed inset-0 bg-transparent backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-black rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white">Meeting Transcription</h2>
                  <button onClick={() => setViewingTranscription(null)} className="text-white hover:text-white">
                    <X size={24} />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Meeting Info */}
                  <div className="bg-white/5 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-white mb-2">{viewingTranscription.title}</h3>
                    <div className="text-sm text-white">
                      {formatDate(viewingTranscription.date)} at {formatTimeTo12Hour(viewingTranscription.time)}
                    </div>
                  </div>

                  {/* Transcript */}
                  {viewingTranscription.transcript && (
                    <div>
                      <label className="block text-sm font-medium text-white mb-3">Transcript</label>
                      <div className="bg-white/5 rounded-lg p-4 max-h-96 overflow-y-auto">
                        <div className="text-white whitespace-pre-wrap font-mono text-sm">
                          {viewingTranscription.transcript}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {viewingTranscription.notes && (
                    <div>
                      <label className="block text-sm font-medium text-white mb-3">Notes</label>
                      <div className="bg-white/5 rounded-lg p-4">
                        <div className="text-white whitespace-pre-wrap">
                          {viewingTranscription.notes}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Summary (if separate) */}
                  {viewingTranscription.transcription_summary && (
                    <div>
                      <label className="block text-sm font-medium text-white mb-3">AI Summary</label>
                      <div className="bg-white/5 rounded-lg p-4">
                        <div className="text-white whitespace-pre-wrap">
                          {viewingTranscription.transcription_summary}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6 pt-6 border-t border-white/10">
                  <button
                    onClick={() => setViewingTranscription(null)}
                    className="w-full bg-transparent hover:bg-white/5 text-white py-3 rounded-lg font-medium transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Meeting Category Management Modal */}
        {showMeetingCategoryModal && (
          <div 
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowMeetingCategoryModal(false)}
          >
            <div 
              className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                <h3 className="text-lg font-bold text-white">Edit Meeting Categories</h3>
                <button
                  onClick={() => setShowMeetingCategoryModal(false)}
                  className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                  data-testid="close-meeting-category-modal-btn"
                >
                  <X size={18} className="text-gray-400" />
                </button>
              </div>
              
              {/* Add New Category */}
              <div className="p-4 border-b border-gray-700">
                <div className="flex gap-2 mb-3">
                  {/* Icon Selector */}
                  <div className="relative">
                    <button
                      type="button"
                      className="w-10 h-10 bg-black border border-gray-600 rounded-lg text-xl flex items-center justify-center hover:border-gray-500"
                      onClick={(e) => {
                        const dropdown = e.currentTarget.nextElementSibling;
                        dropdown.classList.toggle('hidden');
                      }}
                      data-testid="meeting-category-icon-selector"
                    >
                      {newMeetingCategoryIcon}
                    </button>
                    <div className="hidden absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg p-2 z-10 grid grid-cols-6 gap-1 w-48">
                      {meetingCategoryIcons.map((icon) => (
                        <button
                          key={icon}
                          type="button"
                          onClick={(e) => {
                            setNewMeetingCategoryIcon(icon);
                            e.currentTarget.parentElement.classList.add('hidden');
                          }}
                          className="w-7 h-7 hover:bg-gray-700 rounded text-lg flex items-center justify-center"
                        >
                          {icon}
                        </button>
                      ))}
                    </div>
                  </div>
                  <input
                    type="text"
                    value={newMeetingCategoryName}
                    onChange={(e) => setNewMeetingCategoryName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addMeetingCategory()}
                    placeholder="New category name..."
                    className="flex-1 px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                    data-testid="new-meeting-category-input"
                  />
                </div>
                
                {/* Color Selector */}
                <div className="flex gap-2 items-center mb-3">
                  <span className="text-xs text-gray-400">Color:</span>
                  <div className="flex gap-1 flex-wrap">
                    {meetingCategoryColors.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setNewMeetingCategoryColor(color)}
                        className={`w-6 h-6 rounded-full ${getMeetingCategoryColorClasses(color).split(' ')[0]} ${newMeetingCategoryColor === color ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-900' : 'hover:opacity-80'}`}
                        title={color}
                      />
                    ))}
                  </div>
                </div>
                
                <button
                  onClick={addMeetingCategory}
                  disabled={!newMeetingCategoryName.trim()}
                  className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-1.5"
                  data-testid="add-meeting-category-btn"
                >
                  <Plus size={14} />
                  Add Category
                </button>
              </div>
              
              {/* Category List */}
              <div className="p-4 max-h-80 overflow-y-auto space-y-2">
                {customMeetingCategories.filter(c => c.id !== 'all').map((category) => {
                  const protectedCategories = ['real-estate', 'personal'];
                  const isProtected = protectedCategories.includes(category.id);
                  
                  return (
                    <div 
                      key={category.id}
                      className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg group"
                    >
                      {editingMeetingCategory === category.id ? (
                        <>
                          <span className="text-lg">{category.icon}</span>
                          <input
                            type="text"
                            value={editMeetingCategoryValue}
                            onChange={(e) => setEditMeetingCategoryValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') updateMeetingCategory(category.id, editMeetingCategoryValue, category.icon, category.color);
                              if (e.key === 'Escape') {
                                setEditingMeetingCategory(null);
                                setEditMeetingCategoryValue('');
                              }
                            }}
                            className="flex-1 px-2 py-1 bg-black border border-gray-600 rounded text-white text-sm focus:border-purple-500 focus:outline-none"
                            autoFocus
                            data-testid={`edit-meeting-category-input-${category.id}`}
                          />
                          <button
                            onClick={() => updateMeetingCategory(category.id, editMeetingCategoryValue, category.icon, category.color)}
                            className="p-1.5 text-green-400 hover:bg-green-500/20 rounded"
                            title="Save"
                          >
                            <Check size={14} />
                          </button>
                          <button
                            onClick={() => {
                              setEditingMeetingCategory(null);
                              setEditMeetingCategoryValue('');
                            }}
                            className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"
                            title="Cancel"
                          >
                            <X size={14} />
                          </button>
                        </>
                      ) : (
                        <>
                          <span className={`w-8 h-8 rounded-lg ${getMeetingCategoryColorClasses(category.color).split(' ')[0]} flex items-center justify-center text-sm`}>
                            {category.icon}
                          </span>
                          <span className="flex-1 text-white text-sm">
                            {category.name}
                            {isProtected && (
                              <span className="ml-2 text-xs text-gray-500">(default)</span>
                            )}
                          </span>
                          <button
                            onClick={() => {
                              setEditingMeetingCategory(category.id);
                              setEditMeetingCategoryValue(category.name);
                            }}
                            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Edit"
                            data-testid={`edit-meeting-category-btn-${category.id}`}
                          >
                            <Pencil size={14} />
                          </button>
                          {!isProtected && (
                            <button
                              onClick={() => deleteMeetingCategory(category.id)}
                              className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Delete"
                              data-testid={`delete-meeting-category-btn-${category.id}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Modal Footer */}
              <div className="p-4 border-t border-gray-700">
                <button
                  onClick={() => setShowMeetingCategoryModal(false)}
                  className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm font-medium"
                  data-testid="done-meeting-category-btn"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RealEstateManagingPartnerMeetings;
